import axios from "axios";
import { toast } from "sonner";
import { consumeTurnstileToken } from "@/components/TurnstileProvider";

// iter86.38 — deployment hardening. A deployed bundle shipped with the apex
// domain baked in while the app is served from beta.*; the cross-origin CORS
// preflight hit the apex's 301 (browsers reject redirected preflights) and
// every API call died with "Network Error". Frontend and backend always share
// the page origin in both preview and production (/api is ingress-routed), so
// prefer same-origin whenever the baked host differs from the real one.
function _resolveBackendUrl() {
    const raw = process.env.REACT_APP_BACKEND_URL;
    try {
        const loc = window.location;
        const isLocal = ["localhost", "127.0.0.1"].includes(loc.hostname);
        if (!isLocal && (!raw || new URL(raw).host !== loc.host)) {
            return loc.origin;
        }
    } catch { /* non-browser or malformed env — fall through to raw */ }
    return raw;
}
const BACKEND_URL = _resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

// iter84.13 — Endpoints that ask for a Turnstile token. Order matters
// only for clarity. Path matching is done on `config.url` which is
// relative to the axios baseURL (so it starts with `/`).
const TURNSTILE_GATED_PATHS = [
    // POST /api/auth/login — site + platform admin sign-in
    { method: "post", match: (u) => u === "/auth/login" },
    // POST /api/leagues — public league creation (Site Admin creates new hub)
    { method: "post", match: (u) => u === "/leagues" },
    // POST /api/leagues/{id}/participants — league admin add-player
    { method: "post", match: (u) => /^\/leagues\/[^/]+\/participants$/.test(u) },
    // POST /api/league-requests — public "request to join the platform" form
    { method: "post", match: (u) => u === "/league-requests" },
];

function _needsTurnstile(config) {
    const url = config.url || "";
    const method = (config.method || "get").toLowerCase();
    return TURNSTILE_GATED_PATHS.some((g) => g.method === method && g.match(url));
}

const ADMIN_USER_KEY = "pickem_admin_user";  // non-sensitive: { id, email, name, is_super }

// withCredentials lets the browser send the HttpOnly site_admin_token cookie set on login.
// JWT is never stored in JS-accessible storage — eliminates the XSS exfiltration surface.
const client = axios.create({ baseURL: API, withCredentials: true });

// Inject X-Participant-Id header from session on every request so admin-only
// PATCH/DELETE endpoints can verify the caller is the league admin.
client.interceptors.request.use((config) => {
    try {
        const raw = localStorage.getItem("pickem_session_v1");
        if (raw) {
            const s = JSON.parse(raw);
            if (s?.participant_id) {
                config.headers = config.headers || {};
                config.headers["X-Participant-Id"] = s.participant_id;
            }
        }
    } catch { /* ignore */ }
    return config;
});

// iter84.13 — Attach Cloudflare Turnstile token to gated POST endpoints.
// Token is read from the global TurnstileProvider's bag. If no token is
// available the header is omitted — backend in shadow mode tolerates
// this; once we flip TURNSTILE_ENFORCE=true the backend will reject.
client.interceptors.request.use((config) => {
    if (_needsTurnstile(config)) {
        const tok = consumeTurnstileToken();
        if (tok) {
            config.headers = config.headers || {};
            config.headers["cf-turnstile-response"] = tok;
        }
    }
    return config;
});

// iter84.59.7 — Global Pydantic 422 detail coercion. FastAPI / Pydantic
// returns validation errors as `detail: [{loc, msg, type, ...}, ...]`,
// but React renders objects/arrays via toast/components as text and
// crashes with "Objects are not valid as a React child" when a callsite
// does `toast.error(err.response?.data?.detail || "...")` without
// flattening the array. There are 200+ such callsites — patching each
// is impractical. This interceptor mutates `err.response.data.detail`
// in-place into a human-readable string before any callsite reads it,
// so existing `.detail || fallback` patterns Just Work.
//
// iter87.137 — EXCEPTION: dict details that carry a `code` field are the
// platform's structured-error contract (e.g. iter74 `personal_code_required`,
// iter87.133 `consent_required`). Callsites branch on `detail.code` to
// pick which UI to render (modal vs toast). Stringifying those into a
// JSON blob loses the branch, and iter87.133's consent modal never fired
// as a result. Leave dict details with a `code` field alone.
function _stringifyPydanticDetail(detail) {
    if (typeof detail === "string") return detail;
    if (!Array.isArray(detail)) {
        // iter87.137 — preserve structured-error dicts (see docstring).
        if (detail && typeof detail === "object" && typeof detail.code === "string") {
            return detail;
        }
        try { return JSON.stringify(detail); } catch { return String(detail); }
    }
    return detail.map((d) => {
        if (!d || typeof d !== "object") return String(d);
        const loc = Array.isArray(d.loc) ? d.loc.filter(p => p !== "body").join(".") : "";
        const msg = d.msg || d.message || "invalid";
        return loc ? `${loc}: ${msg}` : msg;
    }).join("; ");
}
client.interceptors.response.use(
    (r) => r,
    (err) => {
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail !== "string") {
            const coerced = _stringifyPydanticDetail(detail);
            // iter87.137 — Only overwrite if coercion actually produced a
            // string. Structured-error dicts (`{code, message}`) are
            // returned as-is so callsites can branch on `detail.code`.
            if (typeof coerced === "string") {
                err.response.data.detail = coerced;
            }
        }
        return Promise.reject(err);
    }
);

// 401 handler for admin paths: clear session + toast + redirect/event
client.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err.response?.status;
        const url = err.config?.url || "";
        const isAdminPath = url.startsWith("/site-admin") || url.startsWith("/auth");
        const isLoginAttempt = url === "/auth/login";
        // iter77 — Platform passcode verify intentionally returns 401 on
        // wrong code; do NOT bounce the user out of their session for it.
        const isPasscodeAttempt = url === "/auth/verify-platform-passcode";
        // iter69 — Tenant lifecycle guard. Backend returns 423 Locked with
        // a `lifecycle_state` field when a tenant/league is suspended or
        // archived. Park the metadata in sessionStorage and route the
        // user to the hiatus splash. Re-entering on /hiatus must NOT
        // loop — the page itself avoids calling any guarded endpoint.
        // iter87.408 (user-reported bug) — a CLOSED league's 423
        // (`closed_rivalry_guard.py`) deliberately omits
        // `lifecycle_state` so it does NOT hit this redirect: closed is
        // read-only, not a full lockout, and the admin UI already
        // greys out write controls (`AdminPanel.jsx`'s `isLeagueLocked`)
        // instead — a stray 423 there just surfaces as a toast.
        const body = err.response?.data;
        if (status === 423 && body && body.lifecycle_state) {
            try {
                sessionStorage.setItem("tcr_hiatus_info", JSON.stringify(body));
            } catch (e) { /* storage may be disabled — fall through */ }
            if (window.location.pathname !== "/hiatus") {
                window.location.assign("/hiatus");
            }
            return Promise.reject(err);
        }
        if (status === 401 && isAdminPath && !isLoginAttempt && !isPasscodeAttempt) {
            const hadSession = !!localStorage.getItem(ADMIN_USER_KEY);
            adminSession.clear();
            const path = window.location.pathname;
            // iter85.3 — expire in place: each console (/admin, /platform)
            // shows its OWN login screen instead of bouncing platform
            // admins to the tenant sign-in.
            if (hadSession && (path.startsWith("/admin") || path.startsWith("/platform"))) {
                toast.error("Session expired — please sign in again");
                window.dispatchEvent(new Event("admin-unauthorized"));
            } else if (hadSession) {
                toast.error("Session expired — please sign in again");
                window.location.assign("/admin");
            }
        }
        return Promise.reject(err);
    }
);

export const adminSession = {
    // Persist only non-sensitive profile info; the JWT lives in an HttpOnly cookie.
    save: (_token, admin) => {
        localStorage.setItem(ADMIN_USER_KEY, JSON.stringify(admin));
    },
    load: () => {
        const u = localStorage.getItem(ADMIN_USER_KEY);
        return u ? { admin: JSON.parse(u) } : null;
    },
    clear: () => {
        localStorage.removeItem(ADMIN_USER_KEY);
    },
};

export const api = {
    // Leagues
    createLeague: (data) => client.post("/leagues", data).then(r => r.data),
    getLeague: (id, opts = {}) => {
        const params = {};
        if (opts.spectator) params.spectator = true;
        if (opts.tournamentId) params.tournament_id = opts.tournamentId;
        return client.get(`/leagues/${id}`, { params }).then(r => r.data);
    },
    joinLeague: (data) => client.post("/leagues/join", data).then(r => r.data),
    getLeagueByCode: (code) => client.get(`/leagues/by-code/${code}`).then(r => r.data),
    leaderboard: (id) => client.get(`/leagues/${id}/leaderboard`).then(r => r.data),

    // Tournaments
    listTemplates: () => client.get(`/tournaments/templates`).then(r => r.data),
    createTournament: (data) => client.post(`/tournaments`, data).then(r => r.data),
    getGolfBindings: () => client.get(`/template-bindings/golf`).then(r => r.data),
    getClubSeasonBindings: () => client.get(`/template-bindings/club-season`).then(r => r.data),
    // iter63z3 — Endpoint actually returns {tournament, matches}. Unwrap
    // here so callers get the tournament object directly (matches what
    // every other getX helper does).
    getTournament: (id) => client.get(`/tournaments/${id}`).then(r => r.data?.tournament || r.data),
    addTeam: (id, data) => client.post(`/tournaments/${id}/teams`, data).then(r => r.data),
    removeTeam: (id, teamId) => client.delete(`/tournaments/${id}/teams/${teamId}`).then(r => r.data),
    updateScoring: (id, data) => client.patch(`/tournaments/${id}/scoring`, data).then(r => r.data),
    updateGolfScoring: (id, data) => client.patch(`/golf/tournaments/${id}/scoring`, data).then(r => r.data),
    submitMatch: (id, data) => client.post(`/tournaments/${id}/matches`, data).then(r => r.data),
    updateMatch: (matchId, data) => client.patch(`/matches/${matchId}`, data).then(r => r.data),
    deleteMatch: (matchId) => client.delete(`/matches/${matchId}`).then(r => r.data),
    advanceTeams: (id, data) => client.post(`/tournaments/${id}/advance`, data).then(r => r.data),
    // iter87.199.12 — Picks Audit PDF export. Streams a blob; caller
    // triggers a browser download via createObjectURL.
    downloadPicksAuditPdf: async (tournamentId) => {
        const res = await client.get(
            `/tournaments/${tournamentId}/picks-audit-pdf`,
            { responseType: "blob" },
        );
        const cd = res.headers?.["content-disposition"] || "";
        const m = cd.match(/filename="?([^";]+)"?/i);
        const filename = m ? m[1] : `picks_audit_${tournamentId}.pdf`;
        return { blob: res.data, filename };
    },
    markRoundStarted: (id, data) => client.post(`/tournaments/${id}/mark-round-started`, data).then(r => r.data),
    completeTournament: (id) => client.patch(`/tournaments/${id}/complete`).then(r => r.data),
    updateTournamentDates: (id, body) => client.patch(`/tournaments/${id}/dates`, body).then(r => r.data),
    updateTournamentTz: (id, tz) => client.patch(`/tournaments/${id}/tz`, { tz }).then(r => r.data),
    getSchedule: (id, params = {}) => client.get(`/tournaments/${id}/schedule`, { params }).then(r => r.data),
    getMlbGameHighlights: (tournamentId, gameId) => client.get(`/mlb/tournaments/${tournamentId}/games/${gameId}/highlights`).then(r => r.data),
    // iter87.348 (user-requested) — generic relative-path fetch, used by
    // HighlightsModal/the Box Score button for NFL + club soccer's
    // lazy-resolve endpoints (nfl_engine / api_football_router), so the
    // frontend doesn't need per-sport branching.
    fetchRelative: (path) => client.get(path).then(r => r.data),
    getKoWindows: (template) => client.get(`/ko-windows/${template}`).then(r => r.data),
    recomputeBracket: (tournamentId, participantId) => client.post(
        `/tournaments/${tournamentId}/recompute-bracket`,
        {},
        { headers: participantId ? { "X-Participant-Id": participantId } : {} },
    ).then(r => r.data),
    // iter84 — Dedupe matches + rebuild team stats. Cures the
    // "team played N games but there are only M scheduled" drift.
    recomputeTeamStats: (tournamentId, participantId, { dryRun = false } = {}) => client.post(
        `/tournaments/${tournamentId}/recompute-team-stats${dryRun ? "?dry_run=true" : ""}`,
        {},
        { headers: participantId ? { "X-Participant-Id": participantId } : {} },
    ).then(r => r.data),
    siteAddSchedule: (tid, body) => client.post(`/site-admin/tournaments/${tid}/schedule`, body).then(r => r.data),
    siteEditSchedule: (sid, body) => client.patch(`/site-admin/schedule/${sid}`, body).then(r => r.data),
    siteDeleteSchedule: (sid) => client.delete(`/site-admin/schedule/${sid}`).then(r => r.data),
    siteRefreshSchedule: (tid) => client.post(`/site-admin/tournaments/${tid}/schedule/refresh`).then(r => r.data),
    // iter84.60.4.1 — Hard Wipe (absolute). Deletes ALL fixtures +
    // overrides for the tournament and rebuilds from the current
    // upstream_cache snapshot. Dry-run returns counts + cache snapshot
    // age; wet-run writes a hard_wipe_audit row.
    siteHardWipe: (tid, { dryRun = true } = {}) =>
        client.post(`/site-admin/tournaments/${tid}/hard-wipe`, {
            dry_run: dryRun,
        }).then(r => r.data),
    // iter84.60.4.1 — Rebuild from Cache. Non-destructive: rebuilds
    // fixtures from the current cache snapshot but preserves manual
    // overrides on top.
    siteRebuildFromCache: (tid) =>
        client.post(`/site-admin/tournaments/${tid}/rebuild-from-cache`).then(r => r.data),
    // iter84.60.5.1.5 — Per-tournament upstream_cache observability.
    // Read-only list of recent snapshots (fixtures side, payloads
    // stripped). Works for both ESPN AND API-Football tournaments —
    // that unified view is the whole point of the .60 migration.
    siteUpstreamSnapshots: (tid, { limit = 20 } = {}) =>
        client.get(`/site-admin/tournaments/${tid}/upstream-snapshots`, {
            params: { limit },
        }).then(r => r.data),
    // iter86.11 — Cache management actions. Pin marks a known-good
    // snapshot (also flips is_current + exempts from pruning); rollback
    // = pin + rebuild-from-cache (overrides preserved) in one call.
    sitePinSnapshot: (tid, sid, note) =>
        client.post(`/site-admin/tournaments/${tid}/upstream-snapshots/${sid}/pin`, { note: note || null }).then(r => r.data),
    siteUnpinSnapshot: (tid, sid) =>
        client.post(`/site-admin/tournaments/${tid}/upstream-snapshots/${sid}/unpin`).then(r => r.data),
    siteRollbackToSnapshot: (tid, sid, note) =>
        client.post(`/site-admin/tournaments/${tid}/upstream-snapshots/${sid}/rollback`, { note: note || null }).then(r => r.data),
    // iter87.168 — LLM Monitor read-only endpoints.
    siteLLMMonitorRideStory: ({ limit = 20 } = {}) =>
        client.get(`/site-admin/llm-monitor/ride-story`, { params: { limit } }).then(r => r.data),
    siteLLMMonitorRideStoryDetail: (tid) =>
        client.get(`/site-admin/llm-monitor/ride-story/${tid}`).then(r => r.data),
    // iter87.185 — Wipe cached Ride Story and force a fresh generation.
    // Super-admin only, rate-limited to 6 regens/tournament/hour on the backend.
    siteLLMMonitorRegenerateRideStory: (tid) =>
        client.post(`/site-admin/llm-monitor/ride-story/${tid}/regenerate`).then(r => r.data),
    siteLLMMonitorRivalriesIntel: ({ limit = 20 } = {}) =>
        client.get(`/site-admin/llm-monitor/rivalries-intel`, { params: { limit } }).then(r => r.data),
    // iter87.267 — Series Intel (Playoff Series Rivalries Intel, MLB/NBA).
    siteLLMMonitorSeriesIntel: ({ limit = 20 } = {}) =>
        client.get(`/site-admin/llm-monitor/series-intel`, { params: { limit } }).then(r => r.data),
    // iter87.170 — Universal Pause LLM circuit-breaker admin controls.
    siteLLMMonitorPauseStatus: () =>
        client.get(`/site-admin/llm-monitor/pause-status`).then(r => r.data),
    siteLLMMonitorSetPause: ({ paused, reason }) =>
        client.patch(`/site-admin/llm-monitor/pause`, { paused, reason: reason || null }).then(r => r.data),
    // iter87.199.81 — Site Admin → Admin Ops → Comms Monitor.
    siteCommsMonitorEmailTemplates: () =>
        client.get(`/site-admin/comms-monitor/email-templates`).then(r => r.data),
    siteCommsMonitorEmailPreview: (templateId) =>
        client.get(`/site-admin/comms-monitor/email-preview/${encodeURIComponent(templateId)}`).then(r => r.data),
    siteCommsMonitorScoringRegistry: () =>
        client.get(`/site-admin/comms-monitor/scoring-registry`).then(r => r.data),
    siteCommsMonitorScoringCopy: () =>
        client.get(`/site-admin/comms-monitor/scoring-copy`).then(r => r.data),
    // iter87.428 — To-do reminder email kill switch (Comms Monitor → Email Monitor).
    siteCommsMonitorTodoReminderKillSwitch: () =>
        client.get(`/site-admin/comms-monitor/todo-reminder-kill-switch`).then(r => r.data),
    siteCommsMonitorSetTodoReminderKillSwitch: (emails_live) =>
        client.patch(`/site-admin/comms-monitor/todo-reminder-kill-switch`, { emails_live }).then(r => r.data),
    // iter84.58 — Per-match Round Override (operator wins).
    // round=null clears the override and lets ESPN sync take over again.
    siteOverrideMatchRound: (tid, sid, round) => client.post(
        `/site-admin/tournaments/${tid}/matches/${sid}/override-round`,
        { round },
    ).then(r => r.data),
    // iter87.57 — Round-label ratchet audit retired. Zero tournaments
    // are ESPN-bound; the `blocked_downgrade` writer was removed in
    // iter84.60.4.7 and the last surviving `ambiguous_ko_blocked`
    // writer was retired here. If the ESPN sync ever comes back as a
    // supported upstream, rebuild the audit against a
    // per-fixture-source table (not a single ambiguous `ratchet_events`
    // collection that mixes ESPN + API-Football semantics).
    // iter84.58 (B) — Bulk pin every KO-window scheduled row.
    siteInitKoRoundLabels: (tid) => client.post(
        `/site-admin/tournaments/${tid}/init-ko-round-labels`,
    ).then(r => r.data),
    // iter84.58 (A) — Soft lock every row that currently has a label.
    siteLockCurrentLabels: (tid) => client.post(
        `/site-admin/tournaments/${tid}/schedule/lock-current-labels`,
    ).then(r => r.data),
    // iter84.59 — Tied-KO winner audit. Surfaces every played KO match
    // with a tied regulation score + a winner_id but NO PK scoreline.
    // These are the historical victims of the pre-iter84.59 bug where
    // tied KO games silently advanced team_b as the default winner.
    siteAuditTiedKoMatches: (tid) => client.get(
        `/site-admin/tournaments/${tid}/audit-tied-ko-matches`,
    ).then(r => r.data),
    // iter84.59.10 — Resync a single tied-KO match's PK scoreline from
    // the upstream feed (API-Football today; ESPN deferred). Used by
    // the "Re-sync PK" button on the Tied-KO Audit card to spare the
    // operator from manually entering PKs for games the upstream
    // already carries the data for.
    siteAuditResyncPk: (tid, matchId) => client.post(
        `/site-admin/tournaments/${tid}/audit/resync-pk`,
        { match_id: matchId },
    ).then(r => r.data),
    // iter84.59 — Permanent fix for the leaderboard/Ride divergence.
    // Rebuilds `team.stage_reached`, group W/D/L, GF/GA, eliminated, from
    // the `matches` collection in chronological order. Idempotent on
    // healthy tournaments. Call this when a leaderboard row shows 0
    // points but the Ride chart shows the team gaining rank — that's the
    // exact symptom of a stale `team.stage_reached` after a successful
    // match insert.
    siteRecomputeTeamStats: (tid, dryRun = false) => client.post(
        `/site-admin/tournaments/${tid}/recompute-team-stats`,
        null,
        { params: { dry_run: dryRun } },
    ).then(r => r.data),
    // iter84.59.13 — One-shot heal: re-pull every winner_id for the
    // tournament from the upstream cache (api_football / espn) and then
    // run a full team_stats recompute. Use when historical KO winners
    // disagree with the upstream `winner` flag (typical of tied PK
    // games imported before the iter84.59.12 trust-upstream refactor).
    // Zero quota burn — reads from the local cache only.
    siteRepullUpstreamWinners: (tid, dryRun = false) => client.post(
        `/site-admin/tournaments/${tid}/repull-upstream-winners`,
        null,
        { params: { dry_run: dryRun } },
    ).then(r => r.data),
    // iter84.59.14 Task 3 — Post-Deploy Auto-Verify. Runs a battery
    // of operator-facing checks programmatically (DB reachable,
    // iter84.59.12/13 contracts live, no tied-KO drift, current iter
    // UAT present) and returns pass / warn / fail tiles. Designed to
    // be hit AFTER a production deploy lands; no DB writes, no live
    // upstream API calls.
    postDeployAutoVerify: () => client.post(
        `/site-admin/post-deploy/auto-verify`,
    ).then(r => r.data),
    // League-admin schedule CRUD (X-Participant-Id header)
    leagueAddSchedule: (lid, body, tournamentId) => client.post(`/leagues/${lid}/schedule`, body, { params: tournamentId ? { tournament_id: tournamentId } : {} }).then(r => r.data),
    leagueEditSchedule: (lid, sid, body) => client.patch(`/leagues/${lid}/schedule/${sid}`, body).then(r => r.data),
    leagueDeleteSchedule: (lid, sid) => client.delete(`/leagues/${lid}/schedule/${sid}`).then(r => r.data),
    leagueRefreshSchedule: (lid, tournamentId) => client.post(`/leagues/${lid}/schedule/refresh`, undefined, { params: tournamentId ? { tournament_id: tournamentId } : {} }).then(r => r.data),

    // Drafts
    startDraft: (data) => client.post(`/drafts`, data).then(r => r.data),
    getDraft: (id) => client.get(`/drafts/${id}`).then(r => r.data),
    makePick: (id, data) => client.post(`/drafts/${id}/pick`, data).then(r => r.data),
    clearDraft: (leagueId, tournamentId) => client.delete(
        `/leagues/${leagueId}/draft`,
        { params: tournamentId ? { tournament_id: tournamentId } : {} },
    ).then(r => r.data),

    // Participants
    setKnockoutPick: (data) => client.post(`/participants/knockout-pick`, data).then(r => r.data),
    // iter86.31 — Golf (Slash Golf) endpoints
    // iter86.41 — Season Pot Pick'em (golf pot method for club seasons)
    getSeasonPickemState: (tid) => client.get(`/season-pickem/tournaments/${tid}/state`).then(r => r.data),
    submitSeasonPickemPicks: (tid, participantId, teamIds) =>
        client.post(`/season-pickem/tournaments/${tid}/picks`, { participant_id: participantId, team_ids: teamIds }).then(r => r.data),
    lockSeasonPickemPicks: (tid, locked = true) =>
        client.post(`/season-pickem/tournaments/${tid}/lock-picks`, { locked }).then(r => r.data),
    // iter87.278.2 — swap a pick flagged in `dropped_pot_picks_by_tournament`
    // (its team missed the real playoff field after lock) for a same-pot replacement
    replaceDroppedPotPick: (tid, participantId, oldTeamId, newTeamId) =>
        client.post(`/season-pickem/tournaments/${tid}/replace-dropped-pick`,
            { participant_id: participantId, old_team_id: oldTeamId, new_team_id: newTeamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    // iter87.213 — MLB Playoffs: bracket + Series Predictor + Champions Pick
    getMlbBracket: (tid) => client.get(`/mlb/tournaments/${tid}/bracket`).then(r => r.data),
    submitMlbSeriesPick: (tid, participantId, seriesId, teamId, gameCount) =>
        client.post(`/mlb/tournaments/${tid}/series-picks`,
            { participant_id: participantId, series_id: seriesId, team_id: teamId, game_count: gameCount },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    getMlbChampsState: (tid) => client.get(`/mlb/tournaments/${tid}/champs-state`).then(r => r.data),
    submitMlbChampsPick: (tid, participantId, picks) =>
        client.post(`/mlb/tournaments/${tid}/champs-picks`,
            { participant_id: participantId, ws_team_id: picks.ws },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    // iter87.216 — round-by-round Series Predictor picks breakdown modal
    getMlbSeriesPicksDetail: (tid, participantId) =>
        client.get(`/mlb/tournaments/${tid}/series-picks-detail/${participantId}`).then(r => r.data),
    // iter87.277 — "Clear All Picks" for the Series Predictor modal
    clearMlbSeriesPicks: (tid, participantId, seriesIds) =>
        client.post(`/mlb/tournaments/${tid}/series-picks/clear`,
            { participant_id: participantId, series_ids: seriesIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    // iter87.213.4 — MLB poller cadence + manual run-now (Product Ops parity)
    mlbGetPollerCadence: () => client.get(`/mlb/poller-cadence`).then(r => r.data),
    mlbUpdatePollerCadence: (body) => client.patch(`/mlb/poller-cadence`, body).then(r => r.data),
    mlbGetPollerStatus: () => client.get(`/mlb/poller-status`).then(r => r.data),
    mlbPollerRunNow: () => client.post(`/mlb/poller-run-now`).then(r => r.data),
    // iter87.225 — NBA Playoffs: bracket + Series Predictor + Champions Pick,
    // sibling of the MLB block above.
    getNbaBracket: (tid) => client.get(`/nba/tournaments/${tid}/bracket`).then(r => r.data),
    submitNbaSeriesPick: (tid, participantId, seriesId, teamId, gameCount) =>
        client.post(`/nba/tournaments/${tid}/series-picks`,
            { participant_id: participantId, series_id: seriesId, team_id: teamId, game_count: gameCount },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    getNbaChampsState: (tid) => client.get(`/nba/tournaments/${tid}/champs-state`).then(r => r.data),
    submitNbaChampsPick: (tid, participantId, picks) =>
        client.post(`/nba/tournaments/${tid}/champs-picks`,
            { participant_id: participantId, champion_team_id: picks.champion },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    getNbaSeriesPicksDetail: (tid, participantId) =>
        client.get(`/nba/tournaments/${tid}/series-picks-detail/${participantId}`).then(r => r.data),
    // iter87.277 — "Clear All Picks" for the Series Predictor modal
    clearNbaSeriesPicks: (tid, participantId, seriesIds) =>
        client.post(`/nba/tournaments/${tid}/series-picks/clear`,
            { participant_id: participantId, series_ids: seriesIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    nbaGetPollerCadence: () => client.get(`/nba/poller-cadence`).then(r => r.data),
    nbaUpdatePollerCadence: (body) => client.patch(`/nba/poller-cadence`, body).then(r => r.data),
    nbaGetPollerStatus: () => client.get(`/nba/poller-status`).then(r => r.data),
    nbaPollerRunNow: () => client.post(`/nba/poller-run-now`).then(r => r.data),
    // iter87.230 — NFL/CFB Playoffs: single-elimination bracket (no
    // Series Predictor / Champions Pick — see footballPlayoffsTemplates.js).
    getNflPoBracket: (tid) => client.get(`/nfl-po/tournaments/${tid}/bracket`).then(r => r.data),
    // iter87.374 — NFL Playoffs: Super Bowl Champion (1-2 picks) + Dark
    // Horse Picks (1 per conference), replacing the retired Team Pick
    // Pot draft + Power Pick.
    submitNflChampionPick: (tid, participantId, teamIds) =>
        client.post(`/nfl-po/tournaments/${tid}/champion-pick`,
            { participant_id: participantId, team_ids: teamIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitNflDarkhorsePick: (tid, participantId, conference, teamId) =>
        client.post(`/nfl-po/tournaments/${tid}/darkhorse-pick`,
            { participant_id: participantId, conference, team_id: teamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    getCfbPoBracket: (tid) => client.get(`/cfb-po/tournaments/${tid}/bracket`).then(r => r.data),
    // iter87.437 (user-requested — "mirror NFL exactly") — CFB
    // Playoffs: National Champion (1-2 picks) + Dark Horse Pick
    // (1 pick, no conference split — see CfbDarkhorsePickModal.jsx),
    // replacing the retired Team Pick Pot draft.
    submitCfbChampionPick: (tid, participantId, teamIds) =>
        client.post(`/cfb-po/tournaments/${tid}/champion-pick`,
            { participant_id: participantId, team_ids: teamIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitCfbDarkhorsePick: (tid, participantId, teamId) =>
        client.post(`/cfb-po/tournaments/${tid}/darkhorse-pick`,
            { participant_id: participantId, team_id: teamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    // iter87.361 — NWSL Playoffs: bracket + Champions Pick + Dark Horse
    // Pick + Match Predictor. Pure pick'em (no draft), single admin
    // "nwsl-refresh" endpoint is in site-admin, not here.
    getNwslBracket: (tid) => client.get(`/nwsl/tournaments/${tid}/bracket`).then(r => r.data),
    // iter87.378 (user-requested — "copy the NFL format") — Champions
    // Pick is now 1-or-2 picks, mirroring NFL's Super Bowl Champion.
    submitNwslChampionPick: (tid, participantId, teamIds) =>
        client.post(`/nwsl/tournaments/${tid}/champion-pick`,
            { participant_id: participantId, team_ids: teamIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitNwslDarkhorsePick: (tid, participantId, teamId) =>
        client.post(`/nwsl/tournaments/${tid}/darkhorse-pick`,
            { participant_id: participantId, team_id: teamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitNwslPredictorPick: (tid, participantId, fixtureId, pick) =>
        client.post(`/nwsl/tournaments/${tid}/predictor-pick`,
            { participant_id: participantId, fixture_id: fixtureId, ...pick },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    getNwslPredictorDetail: (tid, participantId) =>
        client.get(`/nwsl/tournaments/${tid}/predictor-picks-detail/${participantId}`).then(r => r.data),
    // iter87.465.20 — MLS Playoffs: bracket + Champions Pick + Dark
    // Horse Pick + Play-In Pick (pre-season) + Series Predictor
    // (Round One, in-season). Playoffs Predictor (Conf Semis+) reuses
    // the generic Matchday Boost endpoints below. Admin "mls-refresh"
    // endpoint is in site-admin, not here.
    getMlsBracket: (tid) => client.get(`/mls/tournaments/${tid}/bracket`).then(r => r.data),
    submitMlsChampionPick: (tid, participantId, teamIds) =>
        client.post(`/mls/tournaments/${tid}/champion-pick`,
            { participant_id: participantId, team_ids: teamIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitMlsDarkhorsePick: (tid, participantId, teamId) =>
        client.post(`/mls/tournaments/${tid}/darkhorse-pick`,
            { participant_id: participantId, team_id: teamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitMlsPlayinPick: (tid, participantId, conference, teamId) =>
        client.post(`/mls/tournaments/${tid}/playin-pick`,
            { participant_id: participantId, conference, team_id: teamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitMlsSeriesPick: (tid, participantId, seriesId, teamId, gameCount) =>
        client.post(`/mls/tournaments/${tid}/series-picks`,
            { participant_id: participantId, series_id: seriesId, team_id: teamId, game_count: gameCount },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    clearMlsSeriesPicks: (tid, participantId, seriesIds) =>
        client.post(`/mls/tournaments/${tid}/series-picks/clear`,
            { participant_id: participantId, series_ids: seriesIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    getMlsSeriesPicksDetail: (tid, participantId) =>
        client.get(`/mls/tournaments/${tid}/series-picks-detail/${participantId}`).then(r => r.data),
    // iter87.466.1 — UCL Playoffs: bracket + UCL Champion + Dark Horse
    // Pick (pre-season) + Advance Predictor (every 2-leg tie) +
    // Champions Predictor (Final only, in-season). Admin "ucl-refresh"
    // endpoint is in site-admin, not here.
    getUclBracket: (tid) => client.get(`/ucl/tournaments/${tid}/bracket`).then(r => r.data),
    submitUclChampionPick: (tid, participantId, teamIds) =>
        client.post(`/ucl/tournaments/${tid}/champion-pick`,
            { participant_id: participantId, team_ids: teamIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitUclDarkhorsePick: (tid, participantId, teamId) =>
        client.post(`/ucl/tournaments/${tid}/darkhorse-pick`,
            { participant_id: participantId, team_id: teamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitUclAdvancePick: (tid, participantId, tieId, teamId) =>
        client.post(`/ucl/tournaments/${tid}/advance-pick`,
            { participant_id: participantId, tie_id: tieId, team_id: teamId },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    clearUclAdvancePicks: (tid, participantId, tieIds) =>
        client.post(`/ucl/tournaments/${tid}/advance-pick/clear`,
            { participant_id: participantId, tie_ids: tieIds },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    submitUclChampionsPredictorPick: (tid, participantId, body) =>
        client.post(`/ucl/tournaments/${tid}/champions-predictor-pick`,
            { participant_id: participantId, ...body },
            { headers: { "X-Participant-Id": participantId } }).then(r => r.data),
    // iter86.43 — Matchday Boost (weekly winner picks for soccer seasons)
    getMatchdayBoostState: (tid, participantId, round) =>
        client.get(`/matchday-boost/tournaments/${tid}/state`, { params: {
            ...(participantId ? { participant_id: participantId } : {}),
            ...(round != null ? { round } : {}),
        } }).then(r => r.data),
    submitMatchdayBoostPicks: (tid, participantId, round, picks) =>
        client.post(`/matchday-boost/tournaments/${tid}/picks`, { participant_id: participantId, round, picks }).then(r => r.data),
    // iter87.467 — Playoff Predictor (NFL/CFB Playoffs + MLS Conf
    // Semis+ only): every round's fixtures in one response, MLB
    // Series Predictor shaped (Open/Locked & Live/Completed).
    getPlayoffPredictorBracket: (tid, participantId) =>
        client.get(`/matchday-boost/tournaments/${tid}/playoff-bracket`, { params: {
            ...(participantId ? { participant_id: participantId } : {}),
        } }).then(r => r.data),
    updateMatchdayBoostSettings: (tid, enabled) =>
        client.patch(`/matchday-boost/tournaments/${tid}/settings`, { enabled }).then(r => r.data),
    // iter87.199.161 — Weekly Spread mid-season start ("Start Tournament: Week X").
    startBoostTournament: (tid) =>
        client.post(`/matchday-boost/tournaments/${tid}/start`).then(r => r.data),
    // iter87.112 — Boost-only templates: change picks per week (locks on first kickoff).
    updateMatchdayBoostPicksPerRound: (tid, picks_per_round) =>
        client.patch(`/matchday-boost/tournaments/${tid}/picks-per-round`, { picks_per_round }).then(r => r.data),
    // iter87.47 — Rivalries Intel v2 (Slice C). Tournament-scoped per
    // iter87.49 — was league-scoped, which caused NFL teams to show
    // for a Premier League tournament in a mixed-sport league.
    // 402 when league is Free tier, 404 when feature flag is off /
    // tournament not eligible. Both errors are treated as "hide the
    // tile" by the caller.
    getRivalriesIntel: (tournamentId, week) =>
        client.get(`/tournaments/${tournamentId}/rivalries-intel`, {
            params: week ? { week } : undefined,
        }).then(r => r.data),
    // iter87.262 — Playoff Series Rivalries Intel (MLB/NBA). 402 when
    // league is Free tier, 404 when feature flag is off / tournament
    // isn't a playoff-series-eligible template. Both are treated as
    // "hide the tile" by the caller. iter87.465.26 — optional `scope`
    // ("series" | "knockout") is MLS-only, fetches one of its two
    // independently-gated intel tiles; omitted for every other sport.
    getPlayoffSeriesIntel: (tournamentId, scope) =>
        client.get(`/tournaments/${tournamentId}/playoff-series-intel`, {
            params: scope ? { scope } : undefined,
        }).then(r => r.data),
    getGolfState: (tournamentId) => client.get(`/golf/tournaments/${tournamentId}/state`).then(r => r.data),
    submitGolfPicks: (tournamentId, participantId, golferIds) =>
        client.post(`/golf/tournaments/${tournamentId}/picks`, { participant_id: participantId, golfer_ids: golferIds }).then(r => r.data),
    golfSync: (tournamentId) => client.post(`/golf/tournaments/${tournamentId}/sync`).then(r => r.data),
    golfConfigStatus: () => client.get(`/slash-golf/config-status`).then(r => r.data),
    getParticipant: (id) => client.get(`/participants/${id}`).then(r => r.data),
    updateParticipantEmail: (id, email) => client.patch(`/participants/${id}/email`, { email }).then(r => r.data),
    updateNotificationPreferences: (id, emailNotificationsEnabled) => client.patch(`/participants/${id}/notification-preferences`, { email_notifications_enabled: emailNotificationsEnabled }).then(r => r.data),
    setAdminParticipation: (leagueId, is_player) => client.patch(`/leagues/${leagueId}/admin-participation`, { is_player }).then(r => r.data),
    setSpectatorMode: (leagueId, enabled) => client.patch(`/leagues/${leagueId}/spectator-mode`, { enabled }).then(r => r.data),
    listLeagueChanges: (leagueId, limit = 10) => client.get(`/leagues/${leagueId}/changes`, { params: { limit } }).then(r => r.data),
    // iter84.23 — Crew identity polish (avatar emoji + crew flair).
    // Self-service only — server enforces caller-is-participant.
    updateMyIdentity: (participantId, body) => client.patch(`/participants/${participantId}/identity`, body).then(r => r.data),

    // Admin custom messages (iter31i)
    postLeagueMessage: (leagueId, text, opts = {}) => client.post(`/leagues/${leagueId}/messages`, {
        text,
        notify_email: !!opts.notifyEmail,
        recipient_scope: opts.recipientScope || "players",
    }).then(r => r.data),
    getMessageRecipientCounts: (leagueId) => client.get(`/leagues/${leagueId}/messages/recipient-counts`).then(r => r.data),
    deleteLeagueMessage: (leagueId, changeId) => client.delete(`/leagues/${leagueId}/messages/${changeId}`).then(r => r.data),

    // Email invites (league admin)
    listInviteEmails: (leagueId) => client.get(`/leagues/${leagueId}/invite-emails`).then(r => r.data),
    sendInviteEmails: (leagueId, emails) => client.post(`/leagues/${leagueId}/invite-emails`, { emails }).then(r => r.data),
    // iter72 — Resend an existing pending invite (uses the original join-invite email).
    resendInviteEmail: (leagueId, inviteId) => client.post(`/leagues/${leagueId}/invite-emails/${inviteId}/resend`).then(r => r.data),
    revokeInviteEmail: (leagueId, inviteId) => client.delete(`/leagues/${leagueId}/invite-emails/${inviteId}`).then(r => r.data),

    // League-admin player management
    adminRenameParticipant: (leagueId, participantId, username) => client.patch(`/leagues/${leagueId}/participants/${participantId}/rename`, { username }).then(r => r.data),
    adminRemoveParticipant: (leagueId, participantId) => client.delete(`/leagues/${leagueId}/participants/${participantId}`).then(r => r.data),
    setTeamsPerParticipant: (leagueId, n) => client.patch(`/leagues/${leagueId}/teams-per-participant`, { teams_per_participant: n }).then(r => r.data),
    // iter47h — tournament-scoped variant (M2). Use when the UI is editing a
    // specific tournament rather than the league's default.
    setTournamentTeamsPerParticipant: (tournamentId, n, participantId) =>
        client.patch(
            `/tournaments/${tournamentId}/teams-per-participant`,
            { teams_per_participant: n },
            { headers: participantId ? { "X-Participant-Id": participantId } : {} },
        ).then(r => r.data),
    // iter49 — Auto-draft capacity preview.
    getDraftCapacity: (tournamentId) =>
        client.get(`/tournaments/${tournamentId}/draft-capacity`).then(r => r.data),
    // iter50 — Player-submitted team rankings (Phase 1). Tournament-scoped.
    getTeamRanking: (leagueId, participantId, tournamentId) =>
        client.get(`/leagues/${leagueId}/participants/${participantId}/team-ranking`, {
            headers: { "X-Participant-Id": participantId },
            params: tournamentId ? { tournament_id: tournamentId } : {},
        }).then(r => r.data),
    setTeamRanking: (leagueId, participantId, teamRanking, tournamentId) =>
        client.put(
            `/leagues/${leagueId}/participants/${participantId}/team-ranking`,
            { team_ranking: teamRanking },
            {
                headers: { "X-Participant-Id": participantId },
                params: tournamentId ? { tournament_id: tournamentId } : {},
            },
        ).then(r => r.data),
    adminAddParticipant: (leagueId, data) => client.post(`/leagues/${leagueId}/participants`, data).then(r => r.data),
    // Personal code (Option B auth) helpers
    getPersonalCode: (leagueId, participantId) => client.get(`/leagues/${leagueId}/participants/${participantId}/personal-code`).then(r => r.data),
    regeneratePersonalCode: (leagueId, participantId, sendEmail = false) => client.post(`/leagues/${leagueId}/participants/${participantId}/regenerate-code`, { send_email: sendEmail }).then(r => r.data),
    resendParticipantRecovery: (leagueId, participantId) => client.post(`/leagues/${leagueId}/participants/${participantId}/resend-recovery`).then(r => r.data),
    // iter87.115 — Bulk send magic-link recovery emails from the Players tab modal.
    bulkResendRecovery: (leagueId, participantIds) => client.post(`/leagues/${leagueId}/participants/bulk-resend-recovery`, { participant_ids: participantIds }).then(r => r.data),

    // Recovery (forgot-username magic link)
    requestRecovery: (data) => client.post(`/participants/request-recovery`, data).then(r => r.data),
    consumeRecovery: (token) => client.post(`/participants/recover/${token}`).then(r => r.data),

    // iter87.194 — Trophy Tokens (per-tournament admin token system).
    trophyTokensGetConfig: (tid) => client.get(`/tournaments/${tid}/trophy-tokens/config`).then(r => r.data),
    trophyTokensPutConfig: (tid, config) => client.put(`/tournaments/${tid}/trophy-tokens/config`, config).then(r => r.data),
    trophyTokensGetRoster: (tid) => client.get(`/tournaments/${tid}/trophy-tokens/roster`).then(r => r.data),
    trophyTokensSetOptIn: (tid, participantId, optedIn) => client.post(`/tournaments/${tid}/trophy-tokens/optin`, { participant_id: participantId, opted_in: optedIn }).then(r => r.data),
    trophyTokensConfirm: (tid, participantId) => client.post(`/tournaments/${tid}/trophy-tokens/confirm`, { participant_id: participantId }).then(r => r.data),
    trophyTokensUnconfirm: (tid, participantId) => client.post(`/tournaments/${tid}/trophy-tokens/unconfirm`, { participant_id: participantId }).then(r => r.data),
    trophyTokensGetPreview: (tid) => client.get(`/tournaments/${tid}/trophy-tokens/preview`).then(r => r.data),
    trophyTokensFinalize: (tid, winnerParticipantIds) => client.post(`/tournaments/${tid}/trophy-tokens/finalize`, { winner_participant_ids: winnerParticipantIds }).then(r => r.data),
    trophyTokensUnfinalize: (tid) => client.post(`/tournaments/${tid}/trophy-tokens/unfinalize`, {}).then(r => r.data),
    trophyTokensAddToSeason: (tid) => client.post(`/tournaments/${tid}/trophy-tokens/add-to-season`, {}).then(r => r.data),
    trophyTokensRemoveFromSeason: (tid) => client.post(`/tournaments/${tid}/trophy-tokens/remove-from-season`, {}).then(r => r.data),
    trophyTokensLockPool: (tid) => client.post(`/tournaments/${tid}/trophy-tokens/lock-pool`, {}).then(r => r.data),
    trophyTokensLeagueLeaderboard: (leagueId) => client.get(`/leagues/${leagueId}/trophy-tokens/leaderboard`).then(r => r.data),

    // iter87.548 — Squares (Phase 1: admin config/game/roster only).
    squaresGetConfig: (tid) => client.get(`/tournaments/${tid}/squares/config`).then(r => r.data),
    squaresPutConfig: (tid, config) => client.put(`/tournaments/${tid}/squares/config`, config).then(r => r.data),
    squaresSearchGames: (tid, q, season) => client.get(`/tournaments/${tid}/squares/games/search`, { params: { q, season } }).then(r => r.data),
    // iter87.554 — pre-creation wizard lookup (no tournament exists yet).
    squaresCuratedGamesWizard: (sport, season) => client.get(`/squares/curated-games`, { params: { sport, season } }).then(r => r.data),
    squaresPutGame: (tid, vendorGameId, season) => client.put(`/tournaments/${tid}/squares/game`, { vendor_game_id: vendorGameId, season }).then(r => r.data),
    squaresGetRoster: (tid) => client.get(`/tournaments/${tid}/squares/roster`).then(r => r.data),
    squaresSetOptIn: (tid, participantId, optedIn) => client.post(`/tournaments/${tid}/squares/optin`, { participant_id: participantId, opted_in: optedIn }).then(r => r.data),
    squaresConfirm: (tid, participantId) => client.post(`/tournaments/${tid}/squares/confirm`, { participant_id: participantId }).then(r => r.data),
    squaresLockPool: (tid) => client.post(`/tournaments/${tid}/squares/lock-pool`, {}).then(r => r.data),
    squaresUnlockPool: (tid) => client.post(`/tournaments/${tid}/squares/unlock-pool`, {}).then(r => r.data),

    // iter87.582 — Squares manual-mode redesign: self opt-in/out, player-
    // chosen allotment, draft-then-batch save-picks, auto-select
    // suggestions, Claim/Unclaim/Clear All, board.
    squaresGetMyState: (tid, participantId) => client.get(`/tournaments/${tid}/squares/config`, { params: { participant_id: participantId } }).then(r => r.data),
    squaresGetBoard: (tid) => client.get(`/tournaments/${tid}/squares/board`).then(r => r.data),
    squaresSelfOptIn: (tid, participantId) => client.post(`/tournaments/${tid}/squares/self-optin`, { participant_id: participantId }).then(r => r.data),
    squaresSelfOptOut: (tid, participantId) => client.post(`/tournaments/${tid}/squares/self-optout`, { participant_id: participantId }).then(r => r.data),
    squaresSetAllotment: (tid, participantId, allotment) => client.post(`/tournaments/${tid}/squares/allotment`, { participant_id: participantId, allotment }).then(r => r.data),
    squaresSavePicks: (tid, participantId, boxIndexes) => client.post(`/tournaments/${tid}/squares/save-picks`, { participant_id: participantId, box_indexes: boxIndexes }).then(r => r.data),
    squaresAutoSelect: (tid, participantId) => client.post(`/tournaments/${tid}/squares/auto-select`, { participant_id: participantId }).then(r => r.data),
    squaresClaimPicks: (tid, participantId) => client.post(`/tournaments/${tid}/squares/claim-picks`, { participant_id: participantId }).then(r => r.data),
    squaresUnclaim: (tid, participantId) => client.post(`/tournaments/${tid}/squares/unclaim`, { participant_id: participantId }).then(r => r.data),
    squaresClearAll: (tid, participantId) => client.post(`/tournaments/${tid}/squares/clear-all`, { participant_id: participantId }).then(r => r.data),

    // iter87.551 — Squares Phase 3: checkpoint scoring + payout ledger.
    squaresGetCheckpoints: (tid) => client.get(`/tournaments/${tid}/squares/checkpoints`).then(r => r.data),
    squaresFinalize: (tid) => client.post(`/tournaments/${tid}/squares/finalize`, {}).then(r => r.data),
    squaresUnfinalize: (tid) => client.post(`/tournaments/${tid}/squares/unfinalize`, {}).then(r => r.data),

    // iter86.90 — ESPN retirement: espn client fns removed (endpoints
    // retained server-side for emergency use only).

    // Movers / Snapshots
    movers: (id, days = 7) => client.get(`/leagues/${id}/movers`, { params: { days } }).then(r => r.data),
    roundMovers: (id, tournamentId) => client.get(`/leagues/${id}/round-movers`, { params: tournamentId ? { tournament_id: tournamentId } : {} }).then(r => r.data),
    // iter45 (M3) — Multi-tournament wiring
    listLeagueTournaments: (leagueId) => client.get(`/leagues/${leagueId}/tournaments`).then(r => r.data),
    updateActiveTournament: (leagueId, tournamentId) =>
        client.patch(`/leagues/${leagueId}/active-tournament`, { tournament_id: tournamentId }).then(r => r.data),
    // iter87.199.6 — `getCrownPoints` + `updateAwardsConfig` helpers
    // removed with the Crown Race excise. Backing endpoints deleted.
    // Title Race replacement helpers will land here when built.
    // iter63z''''''' — Tenant branding (Whitelabel Phase 2 v1).
    getTenantMe: () => client.get(`/tenants/me`).then(r => r.data),
    updateBranding: (payload) => client.put(`/tenant-admin/branding`, payload).then(r => r.data),
    forceSnapshot: (id) => client.post(`/leagues/${id}/snapshot`).then(r => r.data),

    // Site-Admin auth
    authLogin: (data) => client.post(`/auth/login`, data).then(r => r.data),
    authLogout: () => client.post(`/auth/logout`).then(r => r.data),
    authMe: () => client.get(`/auth/me`).then(r => r.data),
    authUpdateMe: (body) => client.patch(`/auth/me`, body).then(r => r.data),
    authChangePassword: (data) => client.post(`/auth/change-password`, data).then(r => r.data),
    authInvite: (data) => client.post(`/auth/invite`, data).then(r => r.data),
    authRequestPasswordReset: (email) => client.post(`/auth/request-password-reset`, { email }).then(r => r.data),
    authResetPassword: (token, new_password) => client.post(`/auth/reset-password`, { token, new_password }).then(r => r.data),
    // iter87.199.43 · TOTP 2FA (backend endpoints landed in iter87.199.42).
    authTotpStatus: () => client.get(`/auth/totp/status`).then(r => r.data),
    authTotpEnroll: () => client.post(`/auth/totp/enroll`).then(r => r.data),
    authTotpEnrollVerify: (code) => client.post(`/auth/totp/enroll/verify`, { code }).then(r => r.data),
    authTotpDisable: (code) => client.post(`/auth/totp/disable`, { code }).then(r => r.data),
    authTotpRegenerateBackupCodes: (code) => client.post(`/auth/totp/regenerate-backup-codes`, { code }).then(r => r.data),
    authLoginTotp: (body) => client.post(`/auth/login-totp`, body).then(r => r.data),
    authSuperAdminResetTotp: (adminId) => client.post(`/platform/admins/${adminId}/totp/reset`).then(r => r.data),
    // iter81 — Slice A.2: Runbook Gate
    runbookGetTemplate: () => client.get(`/release-ops/runbook`).then(r => r.data),
    runbookUpdateTemplate: (body) => client.put(`/release-ops/runbook`, body).then(r => r.data),
    runbookStartPromotion: (body) => client.post(`/release-ops/promotion/start`, body).then(r => r.data),
    // iter84.57.6 — Server fetches latest top-of-main SHA from GitHub
    // and stamps it on the promotion. Called by the CommitGateCard
    // after the operator confirms Save-to-GitHub.
    runbookCaptureSha: (id) => client.post(`/release-ops/promotion/${id}/capture-sha`).then(r => r.data),
    runbookGetPromotion: (id) => client.get(`/release-ops/promotion/${id}`).then(r => r.data),
    runbookUpdateCheck: (id, body) => client.post(`/release-ops/promotion/${id}/check`, body).then(r => r.data),
    runbookArm: (id) => client.post(`/release-ops/promotion/${id}/arm`).then(r => r.data),
    runbookFinalize: (id, confirmation) => client.post(`/release-ops/promotion/${id}/finalize`, { confirmation }).then(r => r.data),
    // iter84.56.6.6 — Pair the Emergent deployment ID with the GitHub SHA.
    runbookSetEmergentDeploymentId: (id, body) =>
        client.post(`/release-ops/promotion/${id}/emergent-deployment-id`, body).then(r => r.data),
    // iter86.33 — Record the dev → main canon merge (PR # / merge SHA) at PROMOTE.
    runbookSetCanonMerge: (id, body) =>
        client.post(`/release-ops/promotion/${id}/canon-merge`, body).then(r => r.data),
    runbookCancelPromotion: (id) => client.post(`/release-ops/promotion/${id}/cancel`).then(r => r.data),
    runbookListPromotions: () => client.get(`/release-ops/promotions`).then(r => r.data),
    // iter82.4 — Phase 3: GitHub-mediated archive
    runbookArchiveList: () => client.get(`/release-ops/runbook-archive`).then(r => r.data),
    runbookRetryArchive: (id) => client.post(`/release-ops/promotion/${id}/retry-archive`).then(r => r.data),
    runbookArchiveFailures: () => client.get(`/release-ops/archive-failures`).then(r => r.data),
    // iter82.4.5 — Slice A.3: GitHub PR/CI/deploys
    githubRecentCommits: () => client.get(`/release-ops/github/recent-commits`).then(r => r.data),
    // iter84.21 — Latest iter heading from CHANGELOG.md, used as a
    // sensible default for the "suggested promotion title" chip.
    changelogLatest: () => client.get(`/release-ops/changelog/latest`).then(r => r.data),
    // iter84.55.1 — Use this for "what iter is at the top of CHANGELOG right
    // now?" (used by the UAT Runbook card). changelogLatest is for "next
    // promotion title" — DIFFERENT semantics, do not interchange.
    changelogTop: () => client.get(`/release-ops/changelog/top`).then(r => r.data),
    // iter84.30 — Tech-debt sweep deltas pending in this deploy
    releaseOpsTechDebtDelta: () => client.get(`/release-ops/tech-debt-delta`).then(r => r.data),
    // iter84.35 — Cross-env drift probe + iter84.34 stale-ARM warning
    releaseOpsProdVersionProbe: () => client.get(`/release-ops/prod-version-probe`).then(r => r.data),
    releaseOpsStaleArmWarning: () => client.get(`/release-ops/stale-arm-warning`).then(r => r.data),
    githubOpenPrs: () => client.get(`/release-ops/github/open-prs`).then(r => r.data),
    githubCanonSync: () => client.get(`/release-ops/github/canon-sync`).then(r => r.data),

    // iter84.52 — Values Engine: 10 deterministic architectural signals.
    // iter84.54 — Surfaced in the Release Ops "Values Engine" card.
    releaseOpsValuesCheck: () => client.get(`/release-ops/values-check`).then(r => r.data),
    // iter84.60.5.7 — Read-only Values Registry (catalog of every values-engine
    // check + release-ops gate). Feeds the Release Ops "Guidance" card.
    releaseOpsValuesRegistry: () => client.get(`/release-ops/values-registry`).then(r => r.data),
    // iter84.60.7 — Pending-classification workflow: steward commits (or
    // flags) a value_group for a PENDING registry entry; append-only audit.
    releaseOpsValuesRegistryClassify: (payload) => client.post(`/release-ops/values-registry/classify`, payload).then(r => r.data),
    releaseOpsValuesRegistryAudit: () => client.get(`/release-ops/values-registry/audit`).then(r => r.data),
    // iter86.66 — Failure Radar: parsed memory/PROCESS_FAILURES.md entries.
    releaseOpsProcessFailures: () => client.get(`/release-ops/process-failures`).then(r => r.data),
    // iter84.60.5.8 — Read-only Obligations Registry (parallel catalog of
    // externally-imposed compliance requirements). Feeds the Guidance tab.
    releaseOpsObligationsRegistry: () => client.get(`/release-ops/obligations-registry`).then(r => r.data),
    // iter84.60.10 — periodic review workflow: steward decisions a
    // review-queue item (bump last_review, optional state/notes change).
    releaseOpsObligationsReview: (payload) => client.post(`/release-ops/obligations-registry/review`, payload).then(r => r.data),
    releaseOpsObligationsAudit: () => client.get(`/release-ops/obligations-registry/audit`).then(r => r.data),
    // iter87.61 Phase 0 · Step 2 — Canon → Security Posture read tab.
    // Reads `/app/memory/security_posture_registry.md §1`. Writes flow
    // through `wf.governance.security-finding` (git PR); no writer here.
    releaseOpsSecurityPostureRegistry: () => client.get(`/release-ops/security-posture-registry`).then(r => r.data),
    // iter87.65 — Commons Console per-user UI preferences (`show_dormant`).
    // Read-only Observer role can also toggle their own view preference; the
    // Commons write-guard has the endpoint on the auth self-service allowlist.
    platformConsolePrefs: () => client.get(`/platform-console/preferences`).then(r => r.data),
    platformConsolePrefsUpdate: (payload) => client.put(`/platform-console/preferences`, payload).then(r => r.data),
    // iter84.61 — Severity governance: registry-governed signal severity,
    // grouped values engine, obligation severity, and high-signal waivers.
    releaseOpsValuesEngine: () => client.get(`/release-ops/values-engine`).then(r => r.data),
    releaseOpsValuesRegistrySeverity: (payload) => client.post(`/release-ops/values-registry/severity`, payload).then(r => r.data),
    releaseOpsObligationsSeverity: (payload) => client.post(`/release-ops/obligations-registry/severity`, payload).then(r => r.data),
    releaseOpsSignalWaivers: () => client.get(`/release-ops/signal-waivers`).then(r => r.data),
    releaseOpsSignalWaiverCreate: (payload) => client.post(`/release-ops/signal-waivers`, payload).then(r => r.data),
    releaseOpsSignalWaiverRevoke: (signalId) => client.delete(`/release-ops/signal-waivers/${signalId}`).then(r => r.data),
    // iter84.63 Phase B — Game Health engine (registry + live posture).
    gameHealthRegistry: () => client.get(`/game-health/registry`).then(r => r.data),
    gameHealthHealers: () => client.get(`/game-health/healers`).then(r => r.data),
    gameHealthSummary: () => client.get(`/game-health/summary`).then(r => r.data),
    adminHealthRegistry: () => client.get(`/admin-health/registry`).then(r => r.data),
    adminHealthSummary: () => client.get(`/admin-health/summary`).then(r => r.data),
    adminHealthConflictingAdminFlagsDetails: () => client.get(`/admin-health/conflicting-admin-flags/details`).then(r => r.data),
    // iter84.59.20 — Sentinel JIT pre-commit check. Re-runs the env-collapse
    // gates in strict (production-mode) semantics and returns `{ok, blockers,
    // signals}`. The CommitGateCard polls this right before unlocking the
    // "I've Saved to GitHub" button so the iter84.46 .env regression cannot
    // make it into a production bundle.
    releaseOpsCommitPrecondition: () => client.get(`/release-ops/commit-precondition`).then(r => r.data),
    // iter84.54 — UAT runbook markdown + per-operator step checkmarks.
    releaseOpsUatRunbook: (iter) => client.get(`/release-ops/uat/${iter}`).then(r => r.data),
    releaseOpsUatChecks: (iter) => client.get(`/release-ops/uat/${iter}/checks`).then(r => r.data),
    releaseOpsUatCheckUpsert: (iter, step_id, checked) =>
        client.post(`/release-ops/uat/${iter}/checks`, { step_id, checked }).then(r => r.data),

    // iter84.56.2 — Test-data pollution inventory (read-only scan for TEST_ prefixed Mongo records).
    releaseOpsTestPollutionInventory: () => client.get(`/release-ops/test-pollution-inventory`).then(r => r.data),
    // iter84.56.2 — Agent pre-flight attestation ledger.
    releaseOpsPreflightAttestations: () => client.get(`/release-ops/preflight-attestations`).then(r => r.data),
    releaseOpsPreflightAttestation: (iter) => client.get(`/release-ops/preflight-attestations/${iter}`).then(r => r.data),
    // iter84.56.4.1 — Orphaned draft picks audit (post iter84.56.4 self-heal cleanup).
    releaseOpsOrphanPicksAudit: () => client.get(`/release-ops/orphan-picks-audit`).then(r => r.data),
    // iter84.56.4.2 — One-shot WCKO stage_reached heal (recover pre-fix tournaments).
    releaseOpsHealWckoStages: () => client.post(`/release-ops/heal-wcko-stages`).then(r => r.data),
    releaseOpsPruneOrphanRefs: (tournamentId) => client.post(`/release-ops/prune-orphan-refs`, { tournament_id: tournamentId }).then(r => r.data),
    // iter84.56.6.1 — One-click pre-deploy rollup of all audit endpoints.
    releaseOpsParitySweep: () => client.get(`/release-ops/parity-sweep`).then(r => r.data),
    releaseOpsStewardshipActions: () => client.get(`/release-ops/stewardship-actions`).then(r => r.data),
    // iter84.56.6.4 — Entry-stage scoring-drift self-service heal.
    releaseOpsScoringDriftPreview: () => client.get(`/release-ops/scoring-drift-preview`).then(r => r.data),
    releaseOpsZeroEntryStageReachPoints: () => client.post(`/release-ops/zero-entry-stage-reach-points`).then(r => r.data),
    // iter84.56.6.5 — Audit Acknowledgement system.
    releaseOpsCreateAuditAck: (body) => client.post(`/release-ops/audit-acks`, body).then(r => r.data),
    releaseOpsRevokeAuditAck: (id) => client.delete(`/release-ops/audit-acks/${id}`).then(r => r.data),
    releaseOpsListAuditAcks: (includeRevoked = false) =>
        client.get(`/release-ops/audit-acks`, { params: { include_revoked: includeRevoked } }).then(r => r.data),

    // iter86.20 — Pulse: LLM crew briefings (governed by pulse_registry.md)
    pulseBriefings: (limit = 10) => client.get(`/pulse/briefings`, { params: { limit } }).then(r => r.data),
    pulseGenerate: (iter = null) => client.post(`/pulse/briefings/generate`, { iter }).then(r => r.data),
    pulseReview: (briefingId, payload) => client.post(`/pulse/briefings/${briefingId}/review`, payload).then(r => r.data),
    pulseReviews: (iter = null) => client.get(`/pulse/reviews`, { params: iter ? { iter } : {} }).then(r => r.data),
    pulseUsage: () => client.get(`/pulse/usage`).then(r => r.data),
    pulseBadge: () => client.get(`/pulse/badge`).then(r => r.data),
    pulseSubscriptions: () => client.get(`/pulse/subscriptions`).then(r => r.data),
    pulseSubscriptionsPut: (subject_id, domains) => client.put(`/pulse/subscriptions`, { subject_id, domains }).then(r => r.data),

    // iter80.2 — exposes feature flags the login UI needs (e.g. platform_2fa_enabled)
    authLoginConfig: () => client.get(`/auth/login-config`).then(r => r.data),
    authListSiteAdmins: () => client.get(`/auth/site-admins`).then(r => r.data),
    authDeleteSiteAdmin: (id) => client.delete(`/auth/site-admins/${id}`).then(r => r.data),
    authBulkDeleteSiteAdmins: (ids) => client.post(`/auth/site-admins/bulk-delete`, { ids }).then(r => r.data),

    // Site-Admin management
    siteStats: () => client.get(`/site-admin/stats`).then(r => r.data),
    siteListLeagues: () => client.get(`/site-admin/leagues`).then(r => r.data),
    // iter87.80 — consolidated Rivalries tab.
    siteRivalriesSummary: (segment = "all", limit = 30) =>
        client.get(`/site-admin/rivalries/summary?segment=${segment}&limit=${limit}`).then(r => r.data),
    siteListCompRequests: () =>
        client.get(`/site-admin/league-requests?type=comp&status=pending`).then(r => r.data),
    siteListParticipants: (leagueId) => client.get(`/site-admin/leagues/${leagueId}/participants`).then(r => r.data),
    siteAddParticipant: (leagueId, data) => client.post(`/site-admin/leagues/${leagueId}/participants`, data).then(r => r.data),
    siteDeleteLeague: (leagueId) => client.delete(`/site-admin/leagues/${leagueId}`).then(r => r.data),
    // iter86.40 — League archive lifecycle (site-admin only)
    siteArchiveLeague: (leagueId) => client.post(`/site-admin/leagues/${leagueId}/archive`).then(r => r.data),
    siteUnarchiveLeague: (leagueId) => client.post(`/site-admin/leagues/${leagueId}/unarchive`).then(r => r.data),
    // iter87.407 (user-requested) — Site-admin-only Close/Unclose. Close
    // is NOT a rivalry-admin action anymore (that's `closeRivalryAsAdmin`
    // below) — this version bypasses every gate (tier, tournament
    // completion) since a site admin has full override authority.
    siteCloseLeague: (leagueId) => client.post(`/site-admin/leagues/${leagueId}/close`).then(r => r.data),
    siteUncloseLeague: (leagueId) => client.post(`/site-admin/leagues/${leagueId}/unclose`).then(r => r.data),
    // iter87.407 — Rivalry admin self-serve Close/Reopen (Rivalry
    // Settings → Danger Zone), any tier, gated on every tournament in
    // the rivalry being complete. Archive is SITE-ADMIN ONLY now — a
    // rivalry admin has no archive button (that was iter87.115's
    // mistake, corrected here); archiving is either the automatic
    // free-tier lifecycle sweep (30 days after closing) or a site admin
    // action from Rivalry Management.
    closeRivalryAsAdmin: (leagueId) => client.post(`/leagues/${leagueId}/close`).then(r => r.data),
    reopenRivalryAsAdmin: (leagueId) => client.post(`/leagues/${leagueId}/reopen`).then(r => r.data),
    // iter87.469 — narrow exception to the above: a FREE-tier admin
    // whose rivalry is already Closed can archive it early instead of
    // waiting out the 30-day countdown, to reuse their 1-rivalry slot.
    archiveRivalryAsAdminEarly: (leagueId) => client.post(`/leagues/${leagueId}/archive-now`).then(r => r.data),
    siteBulkDeleteLeagues: (ids) => client.post(`/site-admin/leagues/bulk-delete`, { ids }).then(r => r.data),
    // iter63z'' — Site Admin creates a league directly (skips public request flow)
    siteCreateLeague: (body) => client.post(`/site-admin/leagues`, body).then(r => r.data),
    // iter63z'' — Runtime poller cadence overrides (Pro plan tuning UI)
    siteGetPollerCadence: () => client.get(`/api-football/poller-cadence`).then(r => r.data),
    siteUpdatePollerCadence: (body) => client.patch(`/api-football/poller-cadence`, body).then(r => r.data),
    nflGetPollerCadence: () => client.get(`/nfl/poller-cadence`).then(r => r.data),
    nflUpdatePollerCadence: (body) => client.patch(`/nfl/poller-cadence`, body).then(r => r.data),
    nflLiveTicker: (tournamentId) => client.get(`/nfl/tournaments/${tournamentId}/live-ticker`).then(r => r.data),
    // iter87.111 — Same live-ticker payload for NFL Weekly Spread Boost.
    nflBoostLiveTicker: (tournamentId) => client.get(`/nfl-boost/tournaments/${tournamentId}/live-ticker`).then(r => r.data),
    // iter63w — Junk/stale league cleanup tool
    siteLeaguesCleanupCandidates: (days = 30) => client.get(`/site-admin/leagues/cleanup-candidates`, { params: { days } }).then(r => r.data),

    // iter67 — Platform Admin tenants management (platform_admin role only)
    listTenants: () => client.get(`/platform-admin/tenants`).then(r => r.data),
    createTenant: (body) => client.post(`/platform-admin/tenants`, body).then(r => r.data),
    updateTenant: (tenantId, body) => client.patch(`/platform-admin/tenants/${tenantId}`, body).then(r => r.data),
    deleteTenant: (tenantId) => client.delete(`/platform-admin/tenants/${tenantId}`).then(r => r.data),

    // iter83 — Tenant Provisioning Wizard: cross-tenant admin invites + public accept
    inviteTenantAdmin: (tenantId, email, name) =>
        client.post(`/platform-admin/tenants/${tenantId}/invite-admin`, { email, name }).then(r => r.data),
    listTenantInvites: (tenantId) =>
        client.get(`/platform-admin/tenants/${tenantId}/invites`).then(r => r.data),
    revokeTenantInvite: (tenantId, tokenPrefix) =>
        client.delete(`/platform-admin/tenants/${tenantId}/invites/${tokenPrefix}`).then(r => r.data),
    acceptAdminInvite: (token, password, name) =>
        client.post(`/auth/accept-admin-invite`, { token, password, name }).then(r => r.data),

    // iter83 — Compliance v1 (Platform Admin: data dictionary, audit-log query, user export)
    complianceDataDictionary: () =>
        client.get(`/platform-admin/compliance/data-dictionary`).then(r => r.data),
    complianceAuditLog: (params = {}) => {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") usp.set(k, v);
        });
        const qs = usp.toString();
        return client.get(`/platform-admin/compliance/audit-log${qs ? `?${qs}` : ""}`).then(r => r.data);
    },
    complianceExportUser: (email) =>
        client.post(`/platform-admin/compliance/export-user`, { email }).then(r => r.data),

    // iter85 — Privacy / CCPA actions (request queue, impact preview, execute, suppression)
    privacyRequestsList: (params = {}) =>
        client.get(`/compliance/privacy-requests`, { params }).then(r => r.data),
    privacyRequestSubmit: (body) =>
        client.post(`/compliance/privacy-requests`, body).then(r => r.data),
    privacyRequestCancel: (rid) =>
        client.post(`/compliance/privacy-requests/${rid}/cancel`).then(r => r.data),
    privacyImpact: (email) =>
        client.get(`/platform-admin/compliance/privacy-impact`, { params: { email } }).then(r => r.data),
    privacyRequestExecute: (rid, confirmEmail) =>
        client.post(`/platform-admin/compliance/privacy-requests/${rid}/execute`, { confirm_email: confirmEmail }).then(r => r.data),
    suppressionList: () =>
        client.get(`/platform-admin/compliance/suppression`).then(r => r.data),
    suppressionAdd: (email, reason) =>
        client.post(`/platform-admin/compliance/suppression`, { email, reason }).then(r => r.data),
    suppressionRemove: (email) =>
        client.delete(`/platform-admin/compliance/suppression/${encodeURIComponent(email)}`).then(r => r.data),

    // iter87.70 — Compliance Ops extensions (reject / restore / suppress-only / unsuppress)
    privacyRequestReject: (rid, rejectionReason) =>
        client.post(`/platform-admin/compliance/privacy-requests/${rid}/reject`, { rejection_reason: rejectionReason }).then(r => r.data),
    privacyRequestRestore: (rid) =>
        client.post(`/platform-admin/compliance/privacy-requests/${rid}/restore`).then(r => r.data),
    privacySuppressOnly: (email, reason) =>
        client.post(`/compliance/privacy-requests/suppress-only`, { email, reason }).then(r => r.data),
    privacyUnsuppress: (email) =>
        client.post(`/compliance/privacy-requests/unsuppress`, { email }).then(r => r.data),

    // iter87.74 — Pro+ v1 request queue + subscription lifecycle
    // iter87.78.iso — Free-tier Pro+ upgrade requests. Called from
    // Site Admin surface (tenant_admin JWT). The Pro+ SELF-SERVICE
    // surface at /rivalry-pro uses its own dedicated client in
    // /app/frontend/src/pro/api.js.
    proPlusSubmit: (notes) =>
        client.post(`/site-admin/pro-plus/requests`, { notes }).then(r => r.data),
    proPlusMine: () =>
        client.get(`/site-admin/pro-plus/requests/mine`).then(r => r.data),
    proPlusListRequests: (params = {}) =>
        client.get(`/site-admin/pro-plus/requests`, { params }).then(r => r.data),
    proPlusApprove: (id) =>
        client.post(`/site-admin/pro-plus/requests/${id}/approve`).then(r => r.data),
    proPlusReject: (id, rejectionReason) =>
        client.post(`/site-admin/pro-plus/requests/${id}/reject`, { rejection_reason: rejectionReason }).then(r => r.data),
    proPlusListSubs: (params = {}) =>
        client.get(`/site-admin/pro-plus/subscriptions`, { params }).then(r => r.data),
    proPlusSuspend: (id, reason, note) =>
        client.post(`/site-admin/pro-plus/subscriptions/${id}/suspend`, { reason, note }).then(r => r.data),
    proPlusReinstate: (id) =>
        client.post(`/site-admin/pro-plus/subscriptions/${id}/reinstate`).then(r => r.data),
    proPlusClose: (id, note) =>
        client.post(`/site-admin/pro-plus/subscriptions/${id}/close`, { note }).then(r => r.data),
    proPlusArchive: (id) =>
        client.post(`/site-admin/pro-plus/subscriptions/${id}/archive`).then(r => r.data),
    proPlusReopen: (id) =>
        client.post(`/site-admin/pro-plus/subscriptions/${id}/reopen`).then(r => r.data),
    proPlusDelete: (id, confirmEmail) =>
        client.post(`/site-admin/pro-plus/subscriptions/${id}/delete`, { confirm_email: confirmEmail }).then(r => r.data),
    // iter87.199.102 — Resend Clerk invitation for a manual-grant subscription.
    proPlusResendInvite: (id) =>
        client.post(`/site-admin/pro-plus/subscriptions/${id}/resend-invite`).then(r => r.data),
    // iter87.199.103 — Remove a subscription from the grandfathered roster.
    proPlusUngrandfather: (id) =>
        client.post(`/site-admin/pro-plus/grandfathered/${id}/ungrandfather`).then(r => r.data),
    proPlusListTesters: () =>
        client.get(`/site-admin/pro-plus/testers`).then(r => r.data),

    // iter87.78.iso — Rivalry Pro+ league helpers removed. The
    // /rivalry-pro surface has its own isolated axios client at
    // /app/frontend/src/pro/api.js — proApi.createLeague / proApi.myLeagues.
    // If site-admin needs to list leagues for a given admin, use
    // /admin/leagues (unchanged).

    // iter85.4 — Vendor & Contract Registry
    vendorsList: () => client.get(`/platform-admin/vendors`).then(r => r.data),
    vendorCreate: (body) => client.post(`/platform-admin/vendors`, body).then(r => r.data),
    vendorUpdate: (id, body) => client.patch(`/platform-admin/vendors/${id}`, body).then(r => r.data),
    vendorDelete: (id) => client.delete(`/platform-admin/vendors/${id}`).then(r => r.data),
    vendorLiveStatus: (id) => client.get(`/platform-admin/vendors/${id}/live-status`).then(r => r.data),
    // iter87.193.3 — Odds API manual sync trigger (POST-only endpoint,
    // used by the FORCE SYNC NOW button on the Vendors tab). Bypasses
    // the double-gate scheduler; each call consumes ~1 upstream call
    // per active sport (5-6 typically) from the 500/month free tier.
    oddsSpreadSyncManual: () => client.post(`/site-admin/odds/spread-sync/manual`).then(r => r.data),
    oddsPlayoffsSyncManual: () => client.post(`/site-admin/odds/playoffs-sync/manual`).then(r => r.data),
    // iter87.465.6 — CFBD manual resync (Product Ops → Tools, CFBD vendor card).
    cfbdResyncManual: () => client.post(`/site-admin/cfbd/resync`).then(r => r.data),
    vendorTestPlan: (id, body) => client.post(`/platform-admin/vendors/${id}/test-plan`, body).then(r => r.data),
    vendorsHealth: () => client.get(`/platform-admin/vendors/health`).then(r => r.data),
    vendorTodos: (status = "open") => client.get(`/platform-admin/vendors/todos?status=${status}`).then(r => r.data),
    vendorTodoCreate: (body) => client.post(`/platform-admin/vendors/todos`, body).then(r => r.data),
    vendorTodoResolve: (id, note) => client.patch(`/platform-admin/vendors/todos/${id}/resolve`, { note }).then(r => r.data),
    featuresRegistry: () => client.get(`/features/registry`).then(r => r.data),
    uatGenerate: (payload) => client.post(`/release-ops/uat/generate`, payload).then(r => r.data),
    devOpsAgreement: () => client.get(`/dev-ops/agreement`).then(r => r.data),
    devOpsAgreementSet: (domainId, body) => client.put(`/dev-ops/agreement/${domainId}`, body).then(r => r.data),
    vendorImpact: (live = false) => client.get(`/game-health/vendor-impact`, { params: live ? { live: true } : {} }).then(r => r.data),

    // iter84.1 — Drift Watch (auto-heal sentinel)
    driftWatchScan: () =>
        client.post(`/platform-admin/drift-watch/scan`).then(r => r.data),
    driftWatchLastScan: () =>
        client.get(`/platform-admin/drift-watch/last-scan`).then(r => r.data),
    // iter84.2 — Bulk recompute every active tournament.
    // iter86.19 — Leaderboard parity heal (Game Health Issues queue).
    gameHealthHealLeaderboardParity: () => client.post(`/game-health/heal-leaderboard-parity`).then(r => r.data),
    gameHealthStaleUnclosedGames: () => client.get(`/game-health/stale-unclosed-games`).then(r => r.data),
    gameHealthMarkGameComplete: (payload) => client.post(`/game-health/stale-unclosed-games/mark-complete`, payload).then(r => r.data),
    gameHealthCloseTournament: (payload) => client.post(`/game-health/stale-unclosed-games/close-tournament`, payload).then(r => r.data),
    gameHealthBulkCloseTournaments: (payload) => client.post(`/game-health/stale-unclosed-games/bulk-close`, payload).then(r => r.data),
    gameHealthOrphanedScheduleRowsPreview: () => client.get(`/game-health/orphaned-schedule-rows`).then(r => r.data),
    gameHealthOrphanedScheduleRowsCleanup: () => client.post(`/game-health/orphaned-schedule-rows/cleanup`).then(r => r.data),
    driftWatchBulkRecompute: ({ dryRun = false } = {}) =>
        client.post(
            `/platform-admin/drift-watch/bulk-recompute${dryRun ? "?dry_run=true" : ""}`,
        ).then(r => r.data),

    // iter71 — Trial Codes CRUD (site-admin, on Subscriptions tab)
    listTrialCodes: () => client.get(`/site-admin/trial-codes`).then(r => r.data),
    createTrialCode: (body) => client.post(`/site-admin/trial-codes`, body).then(r => r.data),
    updateTrialCode: (id, body) => client.patch(`/site-admin/trial-codes/${id}`, body).then(r => r.data),
    deleteTrialCode: (id) => client.delete(`/site-admin/trial-codes/${id}`).then(r => r.data),

    // iter72 — Self-redeem trial code on a free Rivalry to upgrade to comp tier
    redeemTrialCode: (leagueId, code) => client.post(`/leagues/${leagueId}/redeem-trial-code`, { code }).then(r => r.data),

    // iter75 — Product Releases & Roadmap
    listProductReleases: (status) => {
        const params = status ? `?status=${encodeURIComponent(status)}` : "";
        return client.get(`/site-admin/product-releases${params}`).then(r => r.data);
    },
    createProductRelease: (body) => client.post(`/site-admin/product-releases`, body).then(r => r.data),
    updateProductRelease: (id, body) => client.patch(`/site-admin/product-releases/${id}`, body).then(r => r.data),
    deleteProductRelease: (id) => client.delete(`/site-admin/product-releases/${id}`).then(r => r.data),
    whatsNew: (limit = 50) => client.get(`/product-releases/whats-new?limit=${limit}`).then(r => r.data),

    // iter76 — Tenant-level release feed (Tenant Admin "Platform Updates" tab)
    listTenantReleaseFeed: () => client.get(`/tenant-admin/release-feed`).then(r => r.data),
    setTenantForward: (releaseId, forwarded) => client.put(`/tenant-admin/release-feed/${releaseId}/forward`, { forwarded }).then(r => r.data),
    // Platform Admin bulk pre-forward
    bulkForwardReleases: (release_ids, tenant_ids, forwarded = true) =>
        client.post(`/site-admin/product-releases/bulk-forward`, { release_ids, tenant_ids, forwarded }).then(r => r.data),
    // League-scoped What's New (League Admin view)
    leagueWhatsNew: (leagueId, limit = 50) => client.get(`/leagues/${leagueId}/whats-new?limit=${limit}`).then(r => r.data),

    // iter77 — Platform-admin Docs viewer
    listDocs: () => client.get(`/site-admin/docs`).then(r => r.data),
    getDoc: (key) => client.get(`/site-admin/docs/${key}`).then(r => r.data),
    docDownloadUrl: (key) => `${BACKEND_URL}/api/site-admin/docs/${key}/download`,
    // iter87.105 — Issues queue + FAQ deflection.
    getFaqByCategory: (category) => client.get(`/faq`, { params: { category } }).then(r => r.data),
    createIssue: (payload) => client.post(`/issues`, payload).then(r => r.data),
    listIssues: (params = {}) => client.get(`/site-admin/issues`, { params }).then(r => r.data),
    getIssuesBadge: () => client.get(`/site-admin/issues/badge`).then(r => r.data),
    patchIssue: (id, payload) => client.patch(`/site-admin/issues/${id}`, payload).then(r => r.data),
    deleteIssue: (id) => client.delete(`/site-admin/issues/${id}`).then(r => r.data),

    // iter77 — Platform Admin scope passcode gate
    verifyPlatformPasscode: (passcode) => client.post(`/auth/verify-platform-passcode`, { passcode }).then(r => r.data),
    // iter78 — passcode self-service mgmt + email recovery
    getPlatformPasscodeConfig: () => client.get(`/site-admin/platform-passcode-config`).then(r => r.data),
    changePlatformPasscode: (current_passcode, new_passcode) => client.post(`/site-admin/platform-passcode`, { current_passcode, new_passcode }).then(r => r.data),
    setPlatformPasscodeRecoveryEmail: (recovery_email) => client.patch(`/site-admin/platform-passcode-recovery-email`, { recovery_email }).then(r => r.data),
    requestPlatformPasscodeReset: (recovery_email) => client.post(`/auth/request-platform-passcode-reset`, { recovery_email }).then(r => r.data),
    consumePlatformPasscodeReset: (token, new_passcode) => client.post(`/auth/reset-platform-passcode/${token}`, { new_passcode }).then(r => r.data),

    // iter78 — Deployment Readiness Banner (Platform Admin Overview)
    getDeployReadiness: () => client.get(`/site-admin/deploy-readiness`).then(r => r.data),

    // iter80 — Platform identity stack (separate from site_admins)
    platformListUsers: () => client.get(`/platform-admin/users`).then(r => r.data),
    platformListPendingInvites: () => client.get(`/platform-admin/users/pending-invites`).then(r => r.data),
    platformInvite: (email, name, role = "platform_developer") =>
        client.post(`/platform-admin/users/invite`, { email, name, role }).then(r => r.data),
    platformAcceptInvite: (token, password) =>
        client.post(`/platform-admin/users/accept-invite`, { token, password }).then(r => r.data),
    platformSetUserRole: (id, role) =>
        client.patch(`/platform-admin/users/${id}/role`, { role }).then(r => r.data),
    platformSetPermissions: (id, permissions) =>
        client.patch(`/platform-admin/users/${id}/permissions`, { permissions }).then(r => r.data),
    platformDeleteUser: (id) =>
        client.delete(`/platform-admin/users/${id}`).then(r => r.data),
    platformBulkDeleteUsers: (ids) =>
        client.post(`/platform-admin/users/bulk-delete`, { ids }).then(r => r.data),
    platformCancelInvite: (tokenPrefix) =>
        client.delete(`/platform-admin/users/invites/${tokenPrefix}`).then(r => r.data),
    platformResendInvite: (tokenPrefix) =>
        client.post(`/platform-admin/users/invites/${tokenPrefix}/resend`).then(r => r.data),

    // iter86.21 — TCL Registry (read-only upstream commitments) + mapping workflow
    tclRegistry: () => client.get(`/tcl-registry`).then(r => r.data),
    tclMapping: () => client.get(`/release-ops/tcl-mapping`).then(r => r.data),
    tclAdditionDecision: (payload) =>
        client.post(`/release-ops/tcl-mapping/tcl-addition/decision`, payload).then(r => r.data),
    tclAmendmentPropose: (payload) =>
        client.post(`/release-ops/tcl-mapping/tcl-amendment/propose`, payload).then(r => r.data),
    tclAmendmentDecision: (payload) =>
        client.post(`/release-ops/tcl-mapping/tcl-amendment/decision`, payload).then(r => r.data),
    pulseFollowups: (status = "open") =>
        client.get(`/pulse/followups?status=${status}`).then(r => r.data),
    pulseFollowupResolve: (id, payload) =>
        client.post(`/pulse/followups/${id}/resolve`, payload).then(r => r.data),
    pulseIssues: (status = "all") =>
        client.get(`/pulse/issues?status=${status}`).then(r => r.data),
    pulseIssueAcknowledge: (briefingId, key) =>
        client.post(`/pulse/issues/acknowledge`, { briefing_id: briefingId, key }).then(r => r.data),
    pulseIssueAssign: (id, email) =>
        client.post(`/pulse/issues/${id}/assign`, { email }).then(r => r.data),
    pulseIssueStatus: (id, payload) =>
        client.post(`/pulse/issues/${id}/status`, payload).then(r => r.data),
    pulseMemorialize: (payload) =>
        client.post(`/pulse/rollup/memorialize`, payload).then(r => r.data),
    tclMappingConsolidationDecision: (action, note) =>
        client.post(`/release-ops/tcl-mapping/consolidation/decision`, { action, note }).then(r => r.data),
    tclMappingDisposition: (payload) =>
        client.post(`/release-ops/tcl-mapping/disposition`, payload).then(r => r.data),
    tclUpdate: () => client.get(`/release-ops/tcl-update`).then(r => r.data),
    tclUpdateAcknowledge: (note) =>
        client.post(`/release-ops/tcl-update/acknowledge`, { note }).then(r => r.data),
    tclValueCascadeApprove: () =>
        client.post(`/release-ops/tcl-mapping/value-cascade/approve`).then(r => r.data),
    tclValueAmendmentsApprove: () =>
        client.post(`/release-ops/tcl-mapping/value-amendments/approve`).then(r => r.data),
    platformEnvironment: () => client.get(`/release-ops/environment`).then(r => r.data),

    // iter79 — Release Ops (Phase A) — slice A.1 shell endpoint
    releaseOpsOverview: () => client.get(`/release-ops/overview`).then(r => r.data),

    // iter79 — Site admin role management (grant/revoke platform_developer)
    setSiteAdminRole: (id, role) => client.patch(`/auth/site-admins/${id}/role`, { role }).then(r => r.data),

    // iter54 — Data drift sanity check
    siteListOrphanDrafts: () => client.get(`/site-admin/data-drift/orphan-drafts`).then(r => r.data),
    siteHealOrphanDraft: (payload) =>
        client.post(`/site-admin/data-drift/orphan-drafts/heal`, payload).then(r => r.data),

    // iter55 — Draft snapshots (site-admin only)
    siteListDraftSnapshots: (tid) =>
        client.get(`/site-admin/tournaments/${tid}/draft-snapshots`).then(r => r.data),
    siteCaptureDraftSnapshot: (tid) =>
        client.post(`/site-admin/tournaments/${tid}/draft-snapshots/capture`).then(r => r.data),
    siteRestoreDraftSnapshot: (tid, sid, payload) =>
        client.post(`/site-admin/tournaments/${tid}/draft-snapshots/${sid}/restore`, payload).then(r => r.data),
    siteDeleteDraftSnapshot: (tid, sid) =>
        client.delete(`/site-admin/tournaments/${tid}/draft-snapshots/${sid}`).then(r => r.data),

    // Site-Admin subscription tier management (P4 groundwork, iter42)
    siteListSubscriptions: () => client.get(`/site-admin/subscriptions`).then(r => r.data),
    siteSubscriptionsActivity: (limit = 100) =>
        client.get(`/site-admin/subscriptions/activity?limit=${limit}`).then(r => r.data),
    siteSetSubscriptionTier: (leagueId, payload) =>
        client.patch(`/site-admin/leagues/${leagueId}/subscription-tier`, payload).then(r => r.data),
    siteSeedFakeTournament: (leagueId, payload = {}) =>
        client.post(`/site-admin/leagues/${leagueId}/seed-fake-completed-tournament`, payload).then(r => r.data),
    siteDeleteSyntheticTournaments: (leagueId) =>
        client.delete(`/site-admin/leagues/${leagueId}/synthetic-tournaments`).then(r => r.data),
    // iter87.199.6 — `siteGetCrownPoints` + `siteSetCrownPointsTiebreaker`
    // helpers removed with the Crown Race excise. Backing endpoints deleted.
    // iter62e — Site-admin-only GotSport sync toggle (per-league).
    siteSetGotsportEnabled: (leagueId, enabled) =>
        client.patch(`/site-admin/leagues/${leagueId}/gotsport-enabled`, { enabled }).then(r => r.data),

    // iter63a — Site-admin-only API-Football (api-sports.io) toggle (per-league).
    siteSetApiFootballEnabled: (leagueId, enabled) =>
        client.patch(`/site-admin/leagues/${leagueId}/api-football-enabled`, { enabled }).then(r => r.data),

    // iter84.4 — Per-league Data Feed entitlement toggles (Phase 1 of
    // Custom Tournament Data Feed work). Manual entry is always available
    // — no flag needed there. iter86.90: ESPN toggle removed (retired).
    siteSetFeedCsvEnabled: (leagueId, enabled) =>
        client.patch(`/site-admin/leagues/${leagueId}/feed-csv-enabled`, { enabled }).then(r => r.data),

    // iter84.4 (Phase 4) — Site-admin toggle that unlocks Portfolio
    // (overlap-mode) draft for a Free league. Paid/Comp leagues ignore
    // this flag (they always have portfolio available).
    siteSetPortfolioModeUnlocked: (leagueId, enabled) =>
        client.patch(`/site-admin/leagues/${leagueId}/portfolio-mode-unlocked`, { enabled }).then(r => r.data),

    // iter84.4 (Phase 5) — Bulk apply per-league feature flags across
    // multiple selected leagues. Allowed flags: gotsport_enabled,
    // feed_espn_enabled, feed_csv_enabled, portfolio_mode_unlocked.
    siteBulkSetFlags: (leagueIds, flags) =>
        client.post(`/site-admin/leagues/bulk-flags`, { league_ids: leagueIds, flags }).then(r => r.data),

    // iter84.4 (Phase 6) — Per-tenant default league flags. Applied to
    // every NEW league as it's created. Existing leagues unaffected.
    // iter87.85 — Auto-apply retired; endpoint kept alive for legacy data.
    siteGetTenantDefaultFlags: (tenantId) =>
        client.get(`/site-admin/tenants/${tenantId}/default-league-flags`).then(r => r.data),
    siteSetTenantDefaultFlags: (tenantId, flags) =>
        client.patch(`/site-admin/tenants/${tenantId}/default-league-flags`, { flags }).then(r => r.data),

    // iter87.85 — Custom Subscriptions: per-league grant of the 3
    // integration flags (GotSport / CSV / Portfolio). Master switch
    // `custom_enabled` gates whether integrations are editable. Turning
    // it off force-offs all 3 integrations (backend enforces).
    siteListCustomLeagues: (q = "", limit = 50) =>
        client.get(`/site-admin/custom-leagues`, { params: { q, limit } }).then(r => r.data),
    siteSetCustomFeatures: (leagueId, body) =>
        client.patch(`/site-admin/leagues/${leagueId}/custom-features`, body).then(r => r.data),
    // iter84.10 — GotSport auto-sync interval picker. Tournament-scoped.
    setGotsportAutoSync: (tournamentId, intervalMinutes) =>
        client.patch(`/tournaments/${tournamentId}/gotsport-auto-sync`, { interval_minutes: intervalMinutes }).then(r => r.data),

    // iter84.4 — Custom tournament CSV upload (Phase 3). Backend validates
    // the entire file as an aggregate — either all rows succeed or the
    // whole upload is rejected with a grouped error report.
    csvUploadTemplate: (tournamentId) =>
        client.get(`/tournaments/${tournamentId}/csv-upload/template`).then(r => r.data),
    csvUploadMatches: (tournamentId, file, mode = "append") => {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("mode", mode);
        return client.post(`/tournaments/${tournamentId}/csv-upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        }).then(r => r.data);
    },
    siteApiFootballStatus: () =>
        client.get(`/api-football/status`).then(r => r.data),
    siteApiFootballSearchLeagues: (q) =>
        client.get(`/api-football/leagues/search`, { params: { q } }).then(r => r.data),

    // iter63b — API-Football per-tournament import (Custom tournaments only).
    apiFootballPreview: (leagueId, apifLeagueId, season, tournamentId) =>
        client.post(`/leagues/${leagueId}/api-football-preview`, {
            league_id: Number(apifLeagueId), season: Number(season),
            tournament_id: tournamentId || null,
        }).then(r => r.data),
    apiFootballImport: (leagueId, apifLeagueId, season, leagueName, tournamentId) =>
        client.post(`/leagues/${leagueId}/api-football-import`, {
            league_id: Number(apifLeagueId), season: Number(season),
            league_name: leagueName || null, confirm_wipe: true,
            tournament_id: tournamentId || null,
        }).then(r => r.data),
    apiFootballRefresh: (leagueId, tournamentId) =>
        client.post(`/leagues/${leagueId}/api-football-refresh`,
            null,
            { params: tournamentId ? { tournament_id: tournamentId } : {} },
        ).then(r => r.data),
    // iter84.57.2 — One-shot sync diagnosis: compares upstream vs local
    // fixture counts and the poller's last_pass age, returns a verdict
    // ('healthy' / 'pull_recommended' / 'wait_upstream' / 'placeholder_only'
    // / 'poller_stale' / 'not_configured' / 'upstream_unreachable').
    apiFootballSyncDiagnosis: (leagueId, tournamentId) =>
        client.get(`/leagues/${leagueId}/api-football/sync-diagnosis`,
            { params: tournamentId ? { tournament_id: tournamentId } : {} },
        ).then(r => r.data),

    // iter84.57.11 — Passive per-stage fixture-coverage snapshot for
    // the Live Sync card's inline chip. No upstream API call (reads
    // local scheduled_matches only) — safe to fetch on every card
    // mount without burning quota.
    apiFootballCoverage: (leagueId, tournamentId) =>
        client.get(`/leagues/${leagueId}/api-football/coverage`,
            { params: tournamentId ? { tournament_id: tournamentId } : {} },
        ).then(r => r.data),

    // Site-Admin tournament-template management
    // iter86.41.2 — Template draft-pot / tier editor
    siteGetTemplatePots: (key) => client.get(`/site-admin/template-pots/${key}`).then(r => r.data),
    sitePutTemplatePots: (key, teams) => client.put(`/site-admin/template-pots/${key}`, { teams }).then(r => r.data),
    siteListTournamentTemplates: () => client.get(`/site-admin/tournament-templates`).then(r => r.data),
    siteGetTournamentTemplate: (key) => client.get(`/site-admin/tournament-templates/${key}`).then(r => r.data),
    siteSaveTournamentTemplate: (key, body) => client.put(`/site-admin/tournament-templates/${key}`, body).then(r => r.data),
    siteResetTournamentTemplate: (key) => client.delete(`/site-admin/tournament-templates/${key}`).then(r => r.data),
    sitePreviewSchedule: (key, body) => client.post(`/site-admin/tournament-templates/${key}/preview-schedule`, body).then(r => r.data),
    siteSyncMarchMadnessBracket: (body) => client.post(`/site-admin/tournament-templates/march_madness/sync-bracket`, body).then(r => r.data),
    siteListTemplateBindings: () => client.get(`/site-admin/template-bindings`).then(r => r.data),
    siteCreateTemplateBinding: (body) => client.post(`/site-admin/template-bindings`, body).then(r => r.data),
    sitePatchTemplateBinding: (id, body) => client.patch(`/site-admin/template-bindings/${id}`, body).then(r => r.data),
    siteDeleteTemplateBinding: (id) => client.delete(`/site-admin/template-bindings/${id}`).then(r => r.data),
    siteGolfSchedule: (season, tour = "pga") => client.get(`/site-admin/golf/schedule?season=${season}&tour=${tour}`).then(r => r.data),
    siteGetTemplateScoring: (key) => client.get(`/site-admin/template-scoring/${key}`).then(r => r.data),
    sitePutTemplateScoring: (key, scoring) => client.put(`/site-admin/template-scoring/${key}`, { scoring }).then(r => r.data),
    siteResetTemplateScoring: (key) => client.delete(`/site-admin/template-scoring/${key}`).then(r => r.data),
    siteGetGolfPollerCadence: () => client.get(`/slash-golf/poller-cadence`).then(r => r.data),
    siteUpdateGolfPollerCadence: (body) => client.patch(`/slash-golf/poller-cadence`, body).then(r => r.data),
    golfLockPicks: (tournamentId, locked) => client.post(`/golf/tournaments/${tournamentId}/lock-picks`, { locked }).then(r => r.data),
    // iter63s — Site-admin API-Football cache sync + cascade
    siteApifSyncNow: (key) => client.post(`/site-admin/tournament-templates/${key}/api-football-sync-now`).then(r => r.data),
    siteApifCascadePreview: (key) => client.get(`/site-admin/tournament-templates/${key}/api-football-cascade-preview`).then(r => r.data),
    siteApifCascade: (key, body) => client.post(`/site-admin/tournament-templates/${key}/api-football-cascade`, body).then(r => r.data),
    // iter84.29 — Tech-debt watchlist (Platform Console → Tech Debt tab)
    getPlatformTechDebt: () => client.get(`/platform-admin/tech-debt`).then(r => r.data),
    setTechDebtDisposition: (body) => client.patch(`/platform-admin/tech-debt/disposition`, body).then(r => r.data),
    // iter84.25 — Site-admin Teams cascade for non-API-Football templates
    siteTeamsCascadePreview: (key) => client.get(`/site-admin/tournament-templates/${key}/teams-cascade-preview`).then(r => r.data),
    siteTeamsCascade: (key, body) => client.post(`/site-admin/tournament-templates/${key}/teams-cascade`, body).then(r => r.data),
    // iter84.26 — Site-admin Schedule inspector
    siteListTemplateUsage: (key) => client.get(`/site-admin/tournament-templates/${key}/usage`).then(r => r.data),
    siteGetTournamentSchedule: (tournamentId) => client.get(`/site-admin/tournaments/${tournamentId}/schedule`).then(r => r.data),
    siteResyncTournamentFromFeed: (tournamentId) => client.post(`/site-admin/tournaments/${tournamentId}/resync-from-feed`).then(r => r.data),
    siteOverrideMatch: (matchId, body) => client.patch(`/site-admin/matches/${matchId}`, body).then(r => r.data),

    // League requests (public submit + site-admin approval workflow)
    submitLeagueRequest: (body) => client.post(`/league-requests`, body).then(r => r.data),
    acceptTerms: (participantId, termsVersion) => client.post(`/participants/${participantId}/accept-terms`, { terms_version: termsVersion }).then(r => r.data),
    getTermsVersion: () => client.get(`/terms-version`).then(r => r.data),
    getSiteSettings: () => client.get(`/site-settings`).then(r => r.data),
    updateSiteSettings: (patch) => client.patch(`/site-admin/site-settings`, patch).then(r => r.data),
    // iter86.75 — Mobile tester governance (site-admin + platform).
    siteMobileAccessList: () => client.get(`/site-admin/mobile-access`).then(r => r.data),
    siteMobileAccessPatch: (membershipId, active) => client.patch(`/site-admin/mobile-access/${membershipId}`, { active }).then(r => r.data),
    siteMobileAccessBulk: (membershipIds, active) => client.post(`/site-admin/mobile-access/bulk`, { membership_ids: membershipIds, active }).then(r => r.data),
    platMobileTesters: (tenantId) => client.get(`/platform/mobile-testers`, { params: tenantId ? { tenant_id: tenantId } : {} }).then(r => r.data),
    platMobileTesterProgram: (enabled) => client.patch(`/platform/mobile-testers/program`, { enabled }).then(r => r.data),
    platMobileTesterInviteCreate: (note, tenantId) => client.post(`/platform/mobile-testers/invites`, { note, tenant_id: tenantId || null }).then(r => r.data),
    platMobileTesterInviteRevoke: (id) => client.delete(`/platform/mobile-testers/invites/${id}`).then(r => r.data),
    platMobileTesterPatch: (id, patch) => client.patch(`/platform/mobile-testers/${id}`, patch).then(r => r.data),
    platMobileTesterDelete: (id) => client.delete(`/platform/mobile-testers/${id}`).then(r => r.data),
    platMobileTesterClaims: (id) => client.get(`/platform/mobile-testers/${id}/claims`).then(r => r.data),
    // iter86.74 — League-admin custom-feature flags (X-Participant-Id auto-injected).
    leagueSetCustomFeatures: (leagueId, features) =>
        client.patch(`/leagues/${leagueId}/custom-features`, { features }).then(r => r.data),
    // iter63z (template-visibility) — Site-Admin-controlled overrides
    // that gate which template cards league admins see in TournamentSetup.
    // Map: { template_key: "hidden" | "coming_soon" | "live" }. Missing
    // keys fall through to the hardcoded SPORTS/TEMPLATE_PRESETS defaults.
    getTemplateVisibility: () => client.get(`/templates/visibility`).then(r => r.data),
    updateTemplateVisibility: (overrides) =>
        client.put(`/site-admin/templates/visibility`, { overrides }).then(r => r.data),
    // Smack Stream (iter31m, Phase 1)
    // iter63z''''' — Smack is LEAGUE-LEVEL. Endpoints no longer accept a
    // tournament_id; one persistent wall per league across every Challenge.
    listSmack: (leagueId, limit = 10) =>
        client.get(`/leagues/${leagueId}/smack`, { params: { limit } }).then(r => r.data),
    postSmack: (leagueId, body) =>
        client.post(`/leagues/${leagueId}/smack`, { body }).then(r => r.data),
    deleteSmack: (leagueId, msgId) => client.delete(`/leagues/${leagueId}/smack/${msgId}`).then(r => r.data),
    reactSmack: (leagueId, msgId, emoji) =>
        client.post(`/leagues/${leagueId}/smack/${msgId}/reactions`, { emoji }).then(r => r.data),
    toggleSmackStream: (leagueId, enabled) => client.patch(`/leagues/${leagueId}/smack-stream-enabled`, { enabled }).then(r => r.data),
    // iter63z''' — Unread @-mention badge (league-wide as of iter63z''''')
    getSmackUnread: (leagueId) =>
        client.get(`/leagues/${leagueId}/smack/unread-mentions`).then(r => r.data),
    markSmackSeen: (leagueId) => client.post(`/leagues/${leagueId}/smack/mark-seen`).then(r => r.data),
    // iter63z'''''' — Hall of Burn: all-time top 3 posters + top 3 reactors (league-wide).
    getHallOfBurn: (leagueId) => client.get(`/leagues/${leagueId}/smack/hall-of-burn`).then(r => r.data),
    restoreSeedsFromTemplate: (leagueId) => client.post(`/leagues/${leagueId}/restore-seeds-from-template`).then(r => r.data),
    applyFifaTop32: (leagueId) => client.post(`/leagues/${leagueId}/apply-fifa-top32`).then(r => r.data),
    bulkRenameTeams: (leagueId, renames) => client.post(`/leagues/${leagueId}/bulk-rename-teams`, { renames }).then(r => r.data),
    // iter33b — single-team rename with auto-rebind of orphan schedule rows
    renameTeam: (leagueId, teamId, newName) => client.patch(`/leagues/${leagueId}/teams/${teamId}/name`, { name: newName }).then(r => r.data),
    // iter87.177 — Reassign team to a bracket / group / pool. Empty
    // string clears the assignment; server also drops the manual-
    // override flag so next GotSport sync can auto-re-detect.
    updateTeamGroup: (leagueId, teamId, group) => client.patch(`/leagues/${leagueId}/teams/${teamId}/group`, { group }).then(r => r.data),
    listUnmatchedTeams: (leagueId) => client.get(`/leagues/${leagueId}/teams/unmatched`).then(r => r.data),
    // iter34g — toggle 3rd-place playoff scoring (default off; Olympics opts in)
    setCountThirdPlace: (tournamentId, value) =>
        client.patch(`/tournaments/${tournamentId}/count-third-place-playoff`, { count_third_place_playoff: !!value }).then(r => r.data),
    // iter34h — tournament recap data (champion, awards, points-over-time series)
    getRecap: (leagueId, tournamentId) => client.get(`/leagues/${leagueId}/recap`, { params: tournamentId ? { tournament_id: tournamentId } : {} }).then(r => r.data),
    // iter32e — GotSport public-schedule import (Custom tournaments only)
    gotsportListDivisions: (eventId) => client.get(`/gotsport/events/${eventId}/divisions`).then(r => r.data),
    gotsportPreview: (leagueId, eventId, groupId, tournamentId) => client.post(`/leagues/${leagueId}/gotsport-preview`, { event_id: eventId, group_id: groupId, tournament_id: tournamentId || null }).then(r => r.data),
    gotsportImport: (leagueId, eventId, groupId, divisionName, tournamentId) => client.post(`/leagues/${leagueId}/gotsport-import`, { event_id: eventId, group_id: groupId, division_name: divisionName, confirm_wipe: true, tournament_id: tournamentId || null }).then(r => r.data),
    gotsportRefresh: (leagueId, tournamentId) => client.post(`/leagues/${leagueId}/gotsport-refresh`, undefined, { params: tournamentId ? { tournament_id: tournamentId } : {} }).then(r => r.data),
    transferAdmin: (leagueId, newAdminParticipantId, confirmUsername) => client.post(`/leagues/${leagueId}/transfer-admin`, { new_admin_participant_id: newAdminParticipantId, confirm_username: confirmUsername }).then(r => r.data),
    // iter63z''''' — WCKO auto-rename preview (site-admin dry-run + apply)
    siteRenamePreview: (tournamentId) =>
        client.get(`/site-admin/tournaments/${tournamentId}/rename-preview`).then(r => r.data),
    siteRenameApply: (tournamentId, renames) =>
        client.post(`/site-admin/tournaments/${tournamentId}/rename-preview/apply`, { renames }).then(r => r.data),
    // iter63z''''''' — Inject / clear synthetic KO draw for end-to-end QA.
    siteInjectFakeKoDraw: (tournamentId) =>
        client.post(`/site-admin/tournaments/${tournamentId}/inject-fake-ko-draw`).then(r => r.data),
    siteClearFakeKoDraw: (tournamentId) =>
        client.delete(`/site-admin/tournaments/${tournamentId}/inject-fake-ko-draw`).then(r => r.data),
    siteListLeagueRequests: (status, includeArchived = false, type = null) => client.get(`/site-admin/league-requests`, { params: { ...(status ? { status } : {}), ...(includeArchived ? { include_archived: true } : {}), ...(type ? { type } : {}) } }).then(r => r.data),
    siteApproveLeagueRequest: (id) => client.post(`/site-admin/league-requests/${id}/approve`).then(r => r.data),
    siteRejectLeagueRequest: (id, reason) => client.post(`/site-admin/league-requests/${id}/reject`, { reason: reason || null }).then(r => r.data),
    siteArchiveLeagueRequest: (id) => client.post(`/site-admin/league-requests/${id}/archive`).then(r => r.data),
    siteUnarchiveLeagueRequest: (id) => client.post(`/site-admin/league-requests/${id}/unarchive`).then(r => r.data),
    siteArchiveAllRejected: () => client.post(`/site-admin/league-requests/archive-rejected`).then(r => r.data),
    siteAutoArchiveOld: () => client.post(`/site-admin/league-requests/auto-archive-old`).then(r => r.data),
    // iter85.2 — Hard delete for rejected/archived league requests
    siteDeleteLeagueRequest: (id) => client.delete(`/site-admin/league-requests/${id}`).then(r => r.data),
    sitePurgeLeagueRequests: (dryRun = false) => client.post(`/site-admin/league-requests/purge${dryRun ? "?dry_run=true" : ""}`).then(r => r.data),
    siteBulkApproveLeagueRequests: (ids) => client.post(`/site-admin/league-requests/bulk-approve`, { ids }).then(r => r.data),
    siteBulkRejectLeagueRequests: (ids, reason) => client.post(`/site-admin/league-requests/bulk-reject`, { ids, reason: reason || null }).then(r => r.data),
    siteBulkArchiveLeagueRequests: (ids) => client.post(`/site-admin/league-requests/bulk-archive`, { ids }).then(r => r.data),
    // iter86.14 — Permanently delete selected requests (rejected/archived only).
    siteBulkDeleteLeagueRequestsForever: (ids) => client.post(`/site-admin/league-requests/delete-forever`, { ids }).then(r => r.data),
    // Co-admin (iter35a)
    siteGetCoAdminPermissions: () => client.get(`/site-admin/co-admin-permissions`).then(r => r.data),
    siteUpdateCoAdminPermissions: (perms) => client.patch(`/site-admin/co-admin-permissions`, perms).then(r => r.data),
    // iter87.199.198 — Per-league co-admin override layer (primary admin only).
    getCoAdminPermissionOverrides: (leagueId) => client.get(`/leagues/${leagueId}/co-admin-permission-overrides`).then(r => r.data),
    updateCoAdminPermissionOverrides: (leagueId, overrides) => client.patch(`/leagues/${leagueId}/co-admin-permission-overrides`, overrides).then(r => r.data),
    sitePromoteCoAdmin: (leagueId, pid) => client.post(`/site-admin/leagues/${leagueId}/participants/${pid}/promote-co-admin`).then(r => r.data),
    siteDemoteCoAdmin: (leagueId, pid) => client.post(`/site-admin/leagues/${leagueId}/participants/${pid}/demote-co-admin`).then(r => r.data),
    coAdminContext: (leagueId) => client.get(`/leagues/${leagueId}/co-admin-context`).then(r => r.data),
    // Power Pick (iter35d)
    updatePowerPickSettings: (tournamentId, settings) =>
        client.patch(`/tournaments/${tournamentId}/power-pick-settings`, settings).then(r => r.data),
    setPowerPick: (leagueId, participantId, teamId, tournamentId) =>
        client.post(`/leagues/${leagueId}/participants/${participantId}/power-pick`, { team_id: teamId, tournament_id: tournamentId || null }).then(r => r.data),
    clearPowerPick: (leagueId, participantId, tournamentId) =>
        client.delete(`/leagues/${leagueId}/participants/${participantId}/power-pick${tournamentId ? `?tournament_id=${tournamentId}` : ""}`).then(r => r.data),
    // iter35e — strict invites toggle
    updateStrictInvites: (leagueId, value) =>
        client.patch(`/leagues/${leagueId}/strict-invites`, { strict_invites: !!value }).then(r => r.data),
    siteEndTournament: (tid) => client.post(`/site-admin/tournaments/${tid}/end`).then(r => r.data),
    siteReopenTournament: (tid) => client.post(`/site-admin/tournaments/${tid}/reopen`).then(r => r.data),
    siteDeleteTournament: (tid) => client.delete(`/site-admin/tournaments/${tid}`).then(r => r.data),
    // iter86.90 — ESPN retirement: the backfill/recompute/chained-recovery
    // client fns were removed with their cards (endpoints retained
    // server-side for emergency use — game_health_registry #7).
    siteRenameParticipant: (pid, data) => client.patch(`/site-admin/participants/${pid}/rename`, data).then(r => r.data),
    sitePromoteParticipant: (pid, data) => client.patch(`/site-admin/participants/${pid}/promote`, data).then(r => r.data),
    siteRemoveParticipant: (pid) => client.delete(`/site-admin/participants/${pid}`).then(r => r.data),
};

// WebSocket helper — opens a connection per league with auto-reconnect
export function openLeagueSocket(leagueId, onEvent) {
    if (!leagueId) return { close: () => {} };
    const wsProtocol = BACKEND_URL.startsWith("https") ? "wss" : "ws";
    const wsBase = BACKEND_URL.replace(/^https?/, wsProtocol);
    let ws = null;
    let closed = false;
    let retry = 0;

    function connect() {
        try {
            ws = new WebSocket(`${wsBase}/api/ws/leagues/${leagueId}`);
            ws.onopen = () => { retry = 0; };
            ws.onmessage = (e) => {
                try {
                    const msg = JSON.parse(e.data);
                    onEvent?.(msg);
                } catch { /* ignore */ }
            };
            ws.onclose = () => {
                if (closed) return;
                retry = Math.min(retry + 1, 6);
                // Min 1000ms floor prevents reconnect-storm when server briefly rejects
                const delay = Math.max(1000, 1000 * retry);
                setTimeout(connect, delay);
            };
            ws.onerror = () => { try { ws?.close(); } catch { /* ignore */ } };
        } catch {
            if (!closed) setTimeout(connect, 2000);
        }
    }
    connect();
    return { close: () => { closed = true; try { ws?.close(); } catch { /* ignore */ } } };
}

// Local session helpers (no auth, just localStorage)
const KEY = "pickem_session_v1";

export const session = {
    save: ({ league, participant }) => {
        localStorage.setItem(KEY, JSON.stringify({
            league_id: league.id,
            league_name: league.name,
            invite_code: league.invite_code,
            participant_id: participant.id,
            username: participant.username,
            email: participant.email || null,
            is_admin: participant.is_admin,
        }));
    },
    load: () => {
        try {
            const raw = localStorage.getItem(KEY);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    },
    clear: () => localStorage.removeItem(KEY),
};
